import { useState, useEffect, useRef, useCallback } from 'react';
import { generatePDFThumbnail } from './pdfUtils';
import { getApp } from 'firebase/app';
import {
  getFirestore, doc, getDoc, setDoc, addDoc,
  collection, onSnapshot, query, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { getStorage, ref as sRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

// ── Firebase (reuse already-initialized app) ──────────────────────────────────
let _db = null, _storage = null;
function getDB() {
  if (!_db) { try { _db = getFirestore(getApp()); } catch(_) {} }
  return _db;
}
function getSTG() {
  if (!_storage) { try { _storage = getStorage(getApp()); } catch(_) {} }
  return _storage;
}

// ── Theme ─────────────────────────────────────────────────────────────────────
const T = {
  bg:      '#070a10',
  panel:   '#0e131f',
  panel2:  '#131b29',
  border:  '#1e2d44',
  accent:  '#4a8fe0',
  orange:  '#d97706',
  green:   '#2eb87e',
  red:     '#e04a6a',
  yellow:  '#e8b454',
  text:    '#b8ccd8',
  dim:     '#445566',
  bright:  '#dde9f0',
  mono:    "'Share Tech Mono', 'JetBrains Mono', monospace",
  sans:    "'Inter', 'Rajdhani', system-ui, sans-serif",
};

// ── Initial BOM data ──────────────────────────────────────────────────────────
const INITIAL_BOM = [
  { ref:'U1',      cat:'mcu',   desc:'RP2040 Microcontroller (bare IC)',         pn:'RP2040TR13',             pkg:'QFN-56',      qty:1,  volt:'1.8–3.3V',    notes:'Use bare IC for flight. Needs crystal + flash.', inStock:false, stockNote:'' },
  { ref:'Y1',      cat:'mcu',   desc:'12 MHz Crystal',                           pn:'ABM8G-12.000MHZ-B2X-T',  pkg:'3225',        qty:1,  volt:'—',           notes:'Load caps: 15pF×2.', inStock:false, stockNote:'' },
  { ref:'U_FL',    cat:'mcu',   desc:'NOR Flash 2MB SPI',                        pn:'W25Q16JVSSIQ',           pkg:'SOIC-8',      qty:1,  volt:'3.3V',        notes:'Firmware storage for bare RP2040.', inStock:false, stockNote:'' },
  { ref:'F1',      cat:'prot',  desc:'Polyfuse 3A hold, 6A trip',                pn:'MF-MSMF300/24X',         pkg:'1812',        qty:1,  volt:'24V',         notes:'Resettable. On 24V rail input.', inStock:false, stockNote:'' },
  { ref:'D_TVS1',  cat:'prot',  desc:'TVS Diode 24V Unidirectional',             pn:'SMBJ24A-E3/52',          pkg:'SMB',         qty:2,  volt:'38.9V clamp', notes:'One on 24V input, one near coil connectors.', inStock:false, stockNote:'' },
  { ref:'U_BUCK',  cat:'power', desc:'24V→5V Sync Buck 3A',                      pn:'TPS54302DDCT',           pkg:'SOT-23-6',    qty:1,  volt:'24V→5V',      notes:'570kHz. Adj feedback with 1% resistors.', inStock:false, stockNote:'' },
  { ref:'L1',      cat:'power', desc:'Power Inductor 10µH 3A',                   pn:'SRR1260-100Y',           pkg:'SMD 12.4mm',  qty:1,  volt:'—',           notes:'Bourns. Isat >3A.', inStock:false, stockNote:'' },
  { ref:'U_LDO',   cat:'power', desc:'LDO 3.3V 600mA',                           pn:'AP2112K-3.3TRG1',        pkg:'SOT-25',      qty:1,  volt:'5V→3.3V',     notes:'Low noise. Feeds RP2040, sensors.', inStock:false, stockNote:'' },
  { ref:'Q1',      cat:'drive', desc:'N-ch MOSFET 55V 47A Logic-Level',          pn:'IRLZ44NPBF',             pkg:'TO-220',      qty:1,  volt:'55V',         notes:'Coil 1 low-side. Vgs(th)=1–2V. Rds(on)=22mΩ.', inStock:false, stockNote:'' },
  { ref:'U2',      cat:'drive', desc:'Dual MOSFET Gate Driver 1.5A',             pn:'TC4427ACOA713',          pkg:'SOIC-8',      qty:1,  volt:'18V max',     notes:'Drives Q1 from 3.3V PWM. 40ns rise.', inStock:false, stockNote:'' },
  { ref:'D1',      cat:'prot',  desc:'Schottky Flyback Diode 3A 40V',            pn:'SS34',                   pkg:'SMA',         qty:2,  volt:'40V',         notes:'One per coil. Place tight to connector pads.', inStock:false, stockNote:'' },
  { ref:'U4',      cat:'drive', desc:'Single H-Bridge 40V 3A',                   pn:'DRV8874PWPR',            pkg:'HTSSOP-16',   qty:1,  volt:'40V',         notes:'Coil 2 eddy damper. Integrated OCP, thermal, nFAULT.', inStock:false, stockNote:'' },
  { ref:'U3, U5',  cat:'sense', desc:'Current/Voltage Monitor I2C 36V',          pn:'INA226AIDT',             pkg:'SOT-23-8',    qty:2,  volt:'36V max',     notes:'±0.1%. 0x40 (Coil1), 0x41 (Coil2).', inStock:false, stockNote:'' },
  { ref:'R_SH1,2', cat:'sense', desc:'Current Shunt 1mΩ 1W',                     pn:'WSL2010R0010FEA18',      pkg:'2010',        qty:2,  volt:'—',           notes:'Vishay. One per coil.', inStock:false, stockNote:'' },
  { ref:'U6',      cat:'sense', desc:'6-Axis IMU SPI',                           pn:'ICM-42688-P',            pkg:'LGA-14',      qty:1,  volt:'3.3V',        notes:'APEX ZMD zero-velocity detect. ±16g, 32kHz ODR.', inStock:false, stockNote:'' },
  { ref:'U7',      cat:'sense', desc:'ToF LiDAR I2C 4m',                         pn:'VL53L1CBV0FY/1',         pkg:'LGA-12',      qty:1,  volt:'3.3V',        notes:'Up to 4m, ±1mm. 20ms ranging period.', inStock:false, stockNote:'' },
  { ref:'U8',      cat:'mcu',   desc:'Ultra-Low Power Watchdog Timer',           pn:'TPL5010DCNR',            pkg:'SOT-23-6',    qty:1,  volt:'3.3V',        notes:'35µA. Timeout by resistor. Resets RP2040 RUN pin.', inStock:false, stockNote:'' },
  { ref:'R1',      cat:'pass',  desc:'Gate Resistor 10Ω 0402',                   pn:'—',                      pkg:'0402',        qty:3,  volt:'—',           notes:'Q1 gate, DRV IN1/IN2 damping.', inStock:false, stockNote:'' },
  { ref:'R_PU',    cat:'pass',  desc:'Pull-up Resistor 10kΩ 0402',               pn:'—',                      pkg:'0402',        qty:8,  volt:'—',           notes:'nFAULT, Alert, I2C pull-ups (4.7kΩ).', inStock:false, stockNote:'' },
  { ref:'C_bulk',  cat:'pass',  desc:'Bulk Capacitor 100µF 50V Low-ESR',         pn:'EEV-FK1H101Q',           pkg:'Radial D8',   qty:2,  volt:'50V',         notes:'24V input + near DRV8874 VBAT.', inStock:false, stockNote:'' },
  { ref:'C_byp',   cat:'pass',  desc:'Bypass Capacitor 100nF 50V MLCC',          pn:'—',                      pkg:'0402',        qty:20, volt:'—',           notes:'Distribute across all IC power pins. X7R/X5R.', inStock:false, stockNote:'' },
  { ref:'LED1,2',  cat:'pass',  desc:'Status LED Green / Fault LED Red',         pn:'—',                      pkg:'0603',        qty:2,  volt:'3.3V',        notes:'330Ω series. GP14 (status), GP13 (fault).', inStock:false, stockNote:'' },
  { ref:'SW1,SW2', cat:'pass',  desc:'Tact Switch SMD 6mm',                      pn:'TL3342F160QG',           pkg:'SMD 6mm',     qty:2,  volt:'—',           notes:'SW1=Manual Reset, SW2=Safe Mode.', inStock:false, stockNote:'' },
  { ref:'J1',      cat:'pass',  desc:'Power Input Connector 2-pin',              pn:'TE 282834-2',            pkg:'2.54mm',      qty:1,  volt:'24V',         notes:'24V + GND from sat bus.', inStock:false, stockNote:'' },
  { ref:'J2,J3',   cat:'pass',  desc:'Coil Output Connector 2-pin',              pn:'TE 282834-2',            pkg:'2.54mm',      qty:2,  volt:'24V',         notes:'J2=Coil1, J3=Coil2. Rated 5A.', inStock:false, stockNote:'' },
  { ref:'J4',      cat:'pass',  desc:'Debug UART Header 4-pin',                  pn:'—',                      pkg:'2.54mm',      qty:1,  volt:'—',           notes:'TX/RX/3.3V/GND. DNP on flight.', inStock:false, stockNote:'' },
];

const CAT_META = {
  mcu:   { label:'MCU/WDT',  bg:'rgba(232,180,84,0.12)',  color:'#e8b454', border:'rgba(232,180,84,0.3)'  },
  power: { label:'POWER',    bg:'rgba(217,119,6,0.12)',   color:'#d97706', border:'rgba(217,119,6,0.3)'   },
  drive: { label:'DRIVE',    bg:'rgba(74,143,224,0.1)',   color:'#4a8fe0', border:'rgba(74,143,224,0.3)'  },
  sense: { label:'SENSE',    bg:'rgba(46,184,126,0.1)',   color:'#2eb87e', border:'rgba(46,184,126,0.3)'  },
  prot:  { label:'PROTECT',  bg:'rgba(224,74,106,0.1)',   color:'#e04a6a', border:'rgba(224,74,106,0.3)'  },
  pass:  { label:'PASSIVE',  bg:'rgba(68,85,102,0.15)',   color:'#6b8099', border:'rgba(68,85,102,0.3)'   },
};

const UPLOAD_CATS = ['Schematic','PCB Layout','Firmware','Test Data','Photo/Screenshot','Documentation','Other'];

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(ts) {
  if (!ts?.toDate) return ts ? new Date(ts).toLocaleString() : '—';
  return ts.toDate().toLocaleString('en-IN', { dateStyle:'medium', timeStyle:'short' });
}
function fileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes/1024).toFixed(1) + ' KB';
  return (bytes/1048576).toFixed(1) + ' MB';
}
const isImageFile  = n => /\.(png|jpg|jpeg|gif|svg|webp|bmp)$/i.test(n||'');
const isVideoFile  = n => /\.(mp4|webm|mov|avi|mkv|m4v)$/i.test(n||'');
const isAudioFile  = n => /\.(mp3|wav|ogg|m4a|aac|flac|opus)$/i.test(n||'');
const isPDFFile    = n => /\.pdf$/i.test(n||'');
const isGerberFile = n => /\.(gbr|ger|gtl|gbl|gts|gbs|gto|gbo|gtp|gbp|drl|xln|exc|nc|brd|kicad_pcb)$/i.test(n||'');

// ── Shared small components ───────────────────────────────────────────────────
function CatBadge({ cat }) {
  const m = CAT_META[cat] || CAT_META.pass;
  return (
    <span style={{
      display:'inline-block', padding:'2px 8px',
      fontSize:10, fontFamily:T.mono, letterSpacing:0.5,
      background:m.bg, color:m.color, border:`1px solid ${m.border}`, borderRadius:2,
    }}>{m.label}</span>
  );
}

function SectionTitle({ title, sub }) {
  return (
    <div style={{ marginBottom:24 }}>
      <div style={{ fontFamily:T.mono, fontSize:18, fontWeight:700, color:T.accent, letterSpacing:3, textTransform:'uppercase' }}>{title}</div>
      {sub && <div style={{ fontFamily:T.mono, fontSize:11, color:T.dim, letterSpacing:1, marginTop:4 }}>{sub}</div>}
    </div>
  );
}

function InfoBox({ children, color = T.accent }) {
  return (
    <div style={{ border:`1px solid ${color}30`, background:`${color}08`, padding:'12px 16px', marginBottom:16, display:'flex', gap:12 }}>
      <span style={{ color, fontSize:16, flexShrink:0 }}>◈</span>
      <div style={{ fontSize:13, lineHeight:1.6, color:T.text }}>{children}</div>
    </div>
  );
}

function WarnBox({ children }) {
  return (
    <div style={{ border:`1px solid ${T.yellow}40`, background:`${T.yellow}06`, padding:'12px 16px', marginBottom:16, display:'flex', gap:12 }}>
      <span style={{ color:T.yellow, fontSize:16, flexShrink:0 }}>⚠</span>
      <div style={{ fontSize:13, lineHeight:1.6, color:T.text }}>{children}</div>
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{ background:T.panel, border:`1px solid ${T.border}`, padding:20, ...style }}>
      {children}
    </div>
  );
}

function CardTitle({ children, color = T.accent }) {
  return (
    <div style={{
      fontFamily:T.mono, fontSize:10, letterSpacing:2, color:T.dim,
      textTransform:'uppercase', marginBottom:14, paddingBottom:10,
      borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', gap:8,
    }}>
      <span style={{ width:6, height:6, background:color, display:'inline-block' }} />
      {children}
    </div>
  );
}

function SpecTable({ rows }) {
  return (
    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
      <tbody>
        {rows.map(([label, val, cls], i) => (
          <tr key={i} style={{ borderBottom:`1px solid ${T.border}` }}>
            <td style={{ padding:'7px 10px', color:T.dim, fontFamily:T.mono, fontSize:11, width:'50%' }}>{label}</td>
            <td style={{ padding:'7px 10px', color: cls === 'a' ? T.accent : cls === 'o' ? T.orange : cls === 'g' ? T.green : cls === 'y' ? T.yellow : T.text, fontFamily: cls ? T.mono : 'inherit', fontWeight:500 }}>{val}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── EditableCell ──────────────────────────────────────────────────────────────
function EditableCell({ value, onSave, mono: isMono, style: extraStyle }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef();

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  function commit() {
    setEditing(false);
    if (draft !== value) onSave(draft);
  }

  if (editing) return (
    <input
      ref={inputRef}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value); setEditing(false); } }}
      style={{
        width:'100%', background:'#0a1525', border:`1px solid ${T.accent}`,
        color:T.bright, padding:'3px 6px', fontFamily: isMono ? T.mono : T.sans,
        fontSize:12, outline:'none', ...extraStyle,
      }}
    />
  );

  return (
    <span
      onDoubleClick={() => setEditing(true)}
      title="Double-click to edit"
      style={{ cursor:'cell', fontFamily: isMono ? T.mono : T.sans, ...extraStyle }}
    >
      {value || <span style={{ color:T.dim, fontSize:10 }}>—</span>}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  BOM SECTION
// ══════════════════════════════════════════════════════════════════════════════
function BOMSection({ userName }) {
  const [bom, setBom] = useState(INITIAL_BOM);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [filterCat, setFilterCat] = useState('all');
  const [filterStock, setFilterStock] = useState('all');
  const saveTimer = useRef(null);

  // Load from Firestore
  useEffect(() => {
    const db = getDB(); if (!db) return;
    const unsub = onSnapshot(doc(db, 'coil_driver', 'bom'), snap => {
      if (snap.exists() && snap.data().rows) {
        setBom(snap.data().rows);
        setLastSaved(snap.data().updatedAt?.toDate?.() || null);
      }
    });
    return unsub;
  }, []);

  const saveToFirestore = useCallback((rows) => {
    const db = getDB(); if (!db) return;
    setSaving(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await setDoc(doc(db, 'coil_driver', 'bom'), {
          rows, updatedAt: serverTimestamp(), updatedBy: userName || 'embedded',
        });
        setLastSaved(new Date());
      } catch (e) { console.error('BOM save failed:', e); }
      finally { setSaving(false); }
    }, 800);
  }, [userName]);

  function updateCell(idx, field, value) {
    const next = bom.map((r, i) => i === idx ? { ...r, [field]: value } : r);
    setBom(next);
    saveToFirestore(next);
  }

  function toggleStock(idx) {
    const next = bom.map((r, i) => i === idx ? { ...r, inStock: !r.inStock } : r);
    setBom(next);
    saveToFirestore(next);
  }

  const filtered = bom.filter(r =>
    (filterCat === 'all' || r.cat === filterCat) &&
    (filterStock === 'all' || (filterStock === 'in' ? r.inStock : !r.inStock))
  );

  const inStockCount = bom.filter(r => r.inStock).length;

  return (
    <div>
      <SectionTitle title="Bill of Materials" sub="EMMS COIL DRIVER PCB · REV A1 · DOUBLE-CLICK ANY CELL TO EDIT" />

      <WarnBox>
        <strong style={{ color:T.yellow }}>Space Grade Note:</strong> Components are COTS industrial grade for LEO flatsat/proto.
        For flight, screen to MIL-STD-883 or NASA EEE-INST-002. RP2040 not radiation-tested — mitigate with SEU watchdog + TMR.
      </WarnBox>

      {/* Controls */}
      <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ display:'flex', gap:4 }}>
          {['all',...Object.keys(CAT_META)].map(cat => (
            <button key={cat} onClick={() => setFilterCat(cat)} style={{
              padding:'4px 10px', fontFamily:T.mono, fontSize:9, cursor:'pointer', letterSpacing:0.5,
              background: filterCat === cat ? `${T.accent}20` : 'transparent',
              border:`1px solid ${filterCat === cat ? T.accent+'55' : T.border}`,
              color: filterCat === cat ? T.accent : T.dim,
            }}>
              {cat === 'all' ? 'ALL' : CAT_META[cat].label}
            </button>
          ))}
        </div>
        <div style={{ display:'flex', gap:4 }}>
          {[['all','ALL STOCK'],['in','IN STOCK'],['out','NEEDED']].map(([v,l]) => (
            <button key={v} onClick={() => setFilterStock(v)} style={{
              padding:'4px 10px', fontFamily:T.mono, fontSize:9, cursor:'pointer',
              background: filterStock === v ? `${T.green}20` : 'transparent',
              border:`1px solid ${filterStock === v ? T.green+'55' : T.border}`,
              color: filterStock === v ? T.green : T.dim,
            }}>{l}</button>
          ))}
        </div>
        <span style={{ fontFamily:T.mono, fontSize:10, color:T.dim, marginLeft:'auto' }}>
          {inStockCount}/{bom.length} in stock
          {saving && <span style={{ color:T.yellow, marginLeft:8 }}>saving…</span>}
          {!saving && lastSaved && <span style={{ color:T.green, marginLeft:8 }}>saved {new Date(lastSaved).toLocaleTimeString()}</span>}
        </span>
      </div>

      {/* Stock progress bar */}
      <div style={{ height:3, background:T.border, marginBottom:16, borderRadius:2 }}>
        <div style={{ height:'100%', width:`${(inStockCount/bom.length)*100}%`, background:T.green, borderRadius:2, transition:'width 0.3s' }} />
      </div>

      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${T.border}` }}>
              {['Ref','Cat','Description','Part Number','Pkg','Qty','Voltage','Notes','In Stock','Stock Note'].map(h => (
                <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontFamily:T.mono, fontSize:9, letterSpacing:1.5, color:T.dim, background:'#0a1020', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, idx) => {
              const realIdx = bom.indexOf(row);
              return (
                <tr key={row.ref} style={{ borderBottom:`1px solid ${T.border}30` }}
                  onMouseEnter={e => e.currentTarget.style.background = `${T.accent}05`}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding:'7px 10px', fontFamily:T.mono, fontSize:11, color:T.accent }}>
                    <EditableCell value={row.ref} onSave={v => updateCell(realIdx,'ref',v)} mono />
                  </td>
                  <td style={{ padding:'7px 10px' }}><CatBadge cat={row.cat} /></td>
                  <td style={{ padding:'7px 10px', color:T.text, maxWidth:220 }}>
                    <EditableCell value={row.desc} onSave={v => updateCell(realIdx,'desc',v)} />
                  </td>
                  <td style={{ padding:'7px 10px', fontFamily:T.mono, fontSize:11, color:T.green }}>
                    <EditableCell value={row.pn} onSave={v => updateCell(realIdx,'pn',v)} mono />
                  </td>
                  <td style={{ padding:'7px 10px', color:T.dim, fontFamily:T.mono, fontSize:11 }}>
                    <EditableCell value={row.pkg} onSave={v => updateCell(realIdx,'pkg',v)} mono />
                  </td>
                  <td style={{ padding:'7px 10px', color:T.text, textAlign:'center' }}>
                    <EditableCell value={String(row.qty)} onSave={v => updateCell(realIdx,'qty', isNaN(v) ? v : Number(v))} mono />
                  </td>
                  <td style={{ padding:'7px 10px', color:T.dim, fontFamily:T.mono, fontSize:11 }}>
                    <EditableCell value={row.volt} onSave={v => updateCell(realIdx,'volt',v)} mono />
                  </td>
                  <td style={{ padding:'7px 10px', color:T.dim, fontSize:11, maxWidth:200 }}>
                    <EditableCell value={row.notes} onSave={v => updateCell(realIdx,'notes',v)} />
                  </td>
                  <td style={{ padding:'7px 10px', textAlign:'center' }}>
                    <label style={{ display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', gap:4 }}>
                      <input
                        type="checkbox"
                        checked={row.inStock}
                        onChange={() => toggleStock(realIdx)}
                        style={{ accentColor:T.green, width:14, height:14 }}
                      />
                      <span style={{ fontFamily:T.mono, fontSize:9, color: row.inStock ? T.green : T.red }}>
                        {row.inStock ? 'YES' : 'NO'}
                      </span>
                    </label>
                  </td>
                  <td style={{ padding:'7px 10px', minWidth:120 }}>
                    <EditableCell value={row.stockNote} onSave={v => updateCell(realIdx,'stockNote',v)}
                      extraStyle={{ color:T.dim, fontSize:11 }} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop:12, fontFamily:T.mono, fontSize:10, color:T.dim }}>
        {bom.length} unique refs · ~75 total placements · Estimated cost: USD 120–150 (proto qty) ·
        <span style={{ color:T.dim }}> Double-click any cell to edit · Changes auto-save to Firestore</span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  DOCUMENTATION / UPLOADS SECTION
// ══════════════════════════════════════════════════════════════════════════════
function DocsSection({ userName }) {
  const [entries, setEntries] = useState([]);
  const [title, setTitle]     = useState('');
  const [category, setCat]    = useState(UPLOAD_CATS[0]);
  const [notes, setNotes]     = useState('');
  const [file, setFile]       = useState(null);
  const [progress, setProg]   = useState(0);
  const [uploading, setUploading] = useState(false);
  const [err, setErr]         = useState('');
  const [entryType, setEntryType] = useState('file'); // 'file' | 'note'
  const fileRef = useRef();

  // Load documentation entries
  useEffect(() => {
    const db = getDB(); if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'coil_driver_updates'), orderBy('uploadedAt','desc')),
      snap => setEntries(snap.docs.map(d => ({ id:d.id, ...d.data() })))
    );
    return unsub;
  }, []);

  async function handleUpload(e) {
    e.preventDefault();
    if (!title.trim()) { setErr('Title is required'); return; }
    if (entryType === 'file' && !file) { setErr('Select a file to upload'); return; }
    setErr(''); setUploading(true); setProg(0);

    try {
      const db = getDB();
      let fileURL = null, fileName = null, fileSize_ = null, fileType_ = null;

      if (entryType === 'file' && file) {
        const stg = getSTG();
        if (stg) {
          // Firebase Storage upload
          const ts = Date.now();
          const path = `coil_driver/${ts}_${file.name}`;
          const storageReference = sRef(stg, path);
          const task = uploadBytesResumable(storageReference, file);
          await new Promise((resolve, reject) => {
            task.on('state_changed',
              snap => setProg((snap.bytesTransferred / snap.totalBytes) * 100),
              reject,
              async () => { fileURL = await getDownloadURL(task.snapshot.ref); resolve(); }
            );
          });
        } else {
          // Fallback: base64 in Firestore (files < 500KB)
          if (file.size > 500000) { setErr('File too large (>500 KB). Enable Firebase Storage for larger files.'); setUploading(false); return; }
          const buf = await file.arrayBuffer();
          fileURL = 'base64:' + btoa(String.fromCharCode(...new Uint8Array(buf)));
        }
        fileName  = file.name;
        fileSize_ = file.size;
        fileType_ = file.type || 'application/octet-stream';
      }

      // Generate PDF thumbnail if applicable
      let thumbnailURL = null, numPages = null;
      if (entryType === 'file' && file && isPDFFile(file.name) && fileURL && !fileURL.startsWith('base64:')) {
        try {
          const stg = getSTG();
          if (stg) {
            const { thumbnailBlob, numPages: n } = await generatePDFThumbnail(file);
            const thumbName = `${Date.now()}_${file.name.replace(/\.pdf$/i, '_thumb.jpg')}`;
            const thumbRef = sRef(stg, `coil_driver/${thumbName}`);
            const thumbTask = uploadBytesResumable(thumbRef, thumbnailBlob);
            await new Promise((res, rej) => thumbTask.on('state_changed', null, rej, async () => {
              thumbnailURL = await getDownloadURL(thumbTask.snapshot.ref); res();
            }));
            numPages = n;
          }
        } catch(_) { /* proceed without thumbnail */ }
      }

      await addDoc(collection(db, 'coil_driver_updates'), {
        title: title.trim(),
        category,
        notes: notes.trim(),
        type: entryType,
        fileURL, fileName, fileSize: fileSize_, fileType: fileType_,
        thumbnailURL, numPages,
        uploadedBy: userName || 'embedded_engineer',
        uploadedAt: serverTimestamp(),
        revision: 'A1',
      });

      setTitle(''); setNotes(''); setFile(null); setProg(0);
      if (fileRef.current) fileRef.current.value = '';
    } catch (ex) {
      setErr('Upload failed: ' + ex.message);
    } finally {
      setUploading(false);
    }
  }

  const catColors = {
    'Schematic':'#4a8fe0','PCB Layout':'#d97706','Firmware':'#2eb87e',
    'Test Data':'#a78bfa','Photo/Screenshot':'#f97316','Documentation':'#e8b454','Other':'#6b8099',
  };

  return (
    <div>
      <SectionTitle title="Daily Updates & Documentation" sub={`UPLOADED BY: ${(userName||'embedded_engineer').toUpperCase()} · STORED IN FIREBASE`} />

      {/* Upload Form */}
      <Card style={{ marginBottom:20 }}>
        <CardTitle>New Entry</CardTitle>
        <div style={{ display:'flex', gap:8, marginBottom:14 }}>
          {[['file','File Upload'],['note','Text Note']].map(([v,l]) => (
            <button key={v} onClick={() => setEntryType(v)} style={{
              padding:'6px 14px', fontFamily:T.mono, fontSize:10, cursor:'pointer', letterSpacing:1,
              background: entryType === v ? `${T.accent}20` : 'transparent',
              border:`1px solid ${entryType === v ? T.accent : T.border}`,
              color: entryType === v ? T.accent : T.dim,
            }}>{l.toUpperCase()}</button>
          ))}
        </div>

        <form onSubmit={handleUpload}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div>
              <label style={{ fontFamily:T.mono, fontSize:10, color:T.dim, letterSpacing:1, display:'block', marginBottom:4 }}>TITLE *</label>
              <input value={title} onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Schematic Rev A1 — INA226 routing fix"
                style={{ width:'100%', background:'#0a1020', border:`1px solid ${T.border}`, color:T.text,
                  padding:'8px 12px', fontFamily:T.sans, fontSize:13, boxSizing:'border-box', outline:'none' }} />
            </div>
            <div>
              <label style={{ fontFamily:T.mono, fontSize:10, color:T.dim, letterSpacing:1, display:'block', marginBottom:4 }}>CATEGORY</label>
              <select value={category} onChange={e => setCat(e.target.value)}
                style={{ width:'100%', background:'#0a1020', border:`1px solid ${T.border}`, color:T.text,
                  padding:'8px 12px', fontFamily:T.mono, fontSize:12, boxSizing:'border-box' }}>
                {UPLOAD_CATS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {entryType === 'file' && (
            <div style={{ marginBottom:12 }}>
              <label style={{ fontFamily:T.mono, fontSize:10, color:T.dim, letterSpacing:1, display:'block', marginBottom:4 }}>FILE</label>
              <input ref={fileRef} type="file" onChange={e => setFile(e.target.files[0] || null)}
                style={{ fontFamily:T.mono, fontSize:11, color:T.text, background:'#0a1020',
                  border:`1px solid ${T.border}`, padding:'7px 10px', width:'100%', boxSizing:'border-box' }} />
              {file && (
                <div style={{ fontFamily:T.mono, fontSize:10, color:T.dim, marginTop:4 }}>
                  {file.name} · {fileSize(file.size)}
                  {file.size > 500000 && <span style={{ color:T.yellow }}> (requires Firebase Storage)</span>}
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom:12 }}>
            <label style={{ fontFamily:T.mono, fontSize:10, color:T.dim, letterSpacing:1, display:'block', marginBottom:4 }}>NOTES / DESCRIPTION</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              placeholder="What changed, what was tested, design decisions, issues found…"
              style={{ width:'100%', background:'#0a1020', border:`1px solid ${T.border}`, color:T.text,
                padding:'8px 12px', fontFamily:T.sans, fontSize:13, boxSizing:'border-box',
                resize:'vertical', outline:'none' }} />
          </div>

          {uploading && (
            <div style={{ marginBottom:10 }}>
              <div style={{ height:3, background:T.border, borderRadius:2 }}>
                <div style={{ height:'100%', width:`${progress}%`, background:T.accent, borderRadius:2, transition:'width 0.2s' }} />
              </div>
              <div style={{ fontFamily:T.mono, fontSize:10, color:T.accent, marginTop:4 }}>
                Uploading… {progress.toFixed(0)}%
              </div>
            </div>
          )}
          {err && <div style={{ color:T.red, fontFamily:T.mono, fontSize:11, marginBottom:10 }}>{err}</div>}

          <button type="submit" disabled={uploading} style={{
            padding:'9px 24px', background: uploading ? T.border : `${T.accent}20`,
            border:`1px solid ${uploading ? T.border : T.accent}`,
            color: uploading ? T.dim : T.accent, fontFamily:T.mono, fontSize:11,
            letterSpacing:1.5, cursor: uploading ? 'wait' : 'pointer',
          }}>
            {uploading ? 'UPLOADING…' : entryType === 'file' ? 'UPLOAD FILE →' : 'SAVE NOTE →'}
          </button>
        </form>
      </Card>

      {/* Documentation Log */}
      <Card>
        <CardTitle color={T.green}>Documentation Log</CardTitle>
        {entries.length === 0 ? (
          <div style={{ fontFamily:T.mono, fontSize:12, color:T.dim, textAlign:'center', padding:'40px 0' }}>
            No entries yet. Upload your first design file or note above.
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
            {entries.map(e => (
              <div key={e.id} style={{
                padding:'12px 14px', borderLeft:`3px solid ${catColors[e.category] || T.dim}`,
                background:`${catColors[e.category] || T.dim}06`, marginBottom:4,
              }}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, marginBottom:4 }}>
                  <div style={{ flex:1 }}>
                    <span style={{ fontFamily:T.mono, fontSize:12, fontWeight:700, color:T.bright }}>{e.title}</span>
                    <span style={{ fontFamily:T.mono, fontSize:10, color: catColors[e.category] || T.dim,
                      background:`${catColors[e.category] || T.dim}18`, border:`1px solid ${catColors[e.category] || T.dim}40`,
                      padding:'1px 7px', marginLeft:8, borderRadius:2 }}>{e.category}</span>
                    <span style={{ fontFamily:T.mono, fontSize:9, color:T.dim, marginLeft:8 }}>
                      {e.type === 'note' ? '📝 NOTE' : '📎 FILE'}
                    </span>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontFamily:T.mono, fontSize:9, color:T.dim }}>{fmt(e.uploadedAt)}</div>
                    <div style={{ fontFamily:T.mono, fontSize:9, color:T.accent, marginTop:2 }}>
                      {e.uploadedBy}
                    </div>
                  </div>
                </div>
                {e.notes && <div style={{ fontSize:12, color:T.dim, lineHeight:1.5, marginBottom:6 }}>{e.notes}</div>}
                {e.fileName && (
                  <div style={{ marginTop:4 }}>
                    {/* Rich preview */}
                    {e.fileURL && !e.fileURL.startsWith('base64:') && (
                      <div style={{ marginBottom:8 }}>
                        {isImageFile(e.fileName) ? (
                          <img src={e.fileURL} alt={e.fileName}
                            style={{ maxWidth:'100%', maxHeight:240, display:'block', border:`1px solid ${T.border}`, objectFit:'contain', background:'#07090f' }} />
                        ) : isVideoFile(e.fileName) ? (
                          <video src={e.fileURL} controls style={{ maxWidth:'100%', maxHeight:200, display:'block' }} />
                        ) : isAudioFile(e.fileName) ? (
                          <audio src={e.fileURL} controls style={{ width:'100%' }} />
                        ) : isPDFFile(e.fileName) ? (
                          <div style={{ position:'relative', display:'inline-block', width:'100%' }}>
                            {e.thumbnailURL
                              ? <img src={e.thumbnailURL} alt="PDF preview"
                                  style={{ maxWidth:'100%', maxHeight:260, display:'block', border:`1px solid ${T.border}`, objectFit:'contain', background:'#fff' }} />
                              : <div style={{ height:80, background:'#07090f', border:`1px dashed ${T.border}`, display:'flex', alignItems:'center', justifyContent:'center',
                                  fontFamily:T.mono, fontSize:11, color:T.dim }}>📄 PDF</div>
                            }
                            {e.numPages && (
                              <div style={{ position:'absolute', top:6, right:6, background:'rgba(0,0,0,0.65)', color:'#fff',
                                fontFamily:T.mono, fontSize:9, padding:'2px 7px', borderRadius:3 }}>
                                {e.numPages} pages
                              </div>
                            )}
                          </div>
                        ) : isGerberFile(e.fileName) ? (
                          <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'6px 10px',
                            background:`${T.orange}10`, border:`1px solid ${T.orange}40`, fontFamily:T.mono, fontSize:10, color:T.orange }}>
                            🔌 Gerber/PCB File: {e.fileName}
                          </div>
                        ) : null}
                      </div>
                    )}
                    {/* File meta row */}
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      <span style={{ fontFamily:T.mono, fontSize:10, color:T.text }}>{e.fileName}</span>
                      {e.fileSize && <span style={{ fontFamily:T.mono, fontSize:9, color:T.dim }}>{fileSize(e.fileSize)}</span>}
                      {e.fileURL && !e.fileURL.startsWith('base64:') && (
                        <a href={e.fileURL} target="_blank" rel="noreferrer"
                          style={{ fontFamily:T.mono, fontSize:9, color:T.accent, textDecoration:'none',
                            border:`1px solid ${T.accent}44`, padding:'2px 8px' }}>
                          {isPDFFile(e.fileName) ? '📄 OPEN PDF ↗' : 'DOWNLOAD ↗'}
                        </a>
                      )}
                      {e.fileURL?.startsWith('base64:') && (
                        <span style={{ fontFamily:T.mono, fontSize:9, color:T.green }}>STORED (base64)</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  OVERVIEW SECTION
// ══════════════════════════════════════════════════════════════════════════════
function OverviewSection() {
  return (
    <div>
      <SectionTitle title="System Overview" sub="EMMS DUAL-COIL DRIVER · 24V / 3A · RP2040 · SPACE-GRADE LEO" />
      <InfoBox>
        This board drives the <strong style={{color:T.accent}}>Electromagnetic Mass Shifting System (EMMS)</strong> for the EP-ADCS thruster.
        It sequences two coils — a <strong style={{color:T.accent}}>drive electromagnet (Coil 1)</strong> that fires for 100 ms and an{' '}
        <strong style={{color:T.orange}}>eddy current damper (Coil 2)</strong> that fires 20 ms after,
        with switchable DC polarity to generate a changing magnetic field.
        Power level is closed-loop controlled from LiDAR/laser mass-position feedback.
      </InfoBox>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <Card>
          <CardTitle>Electrical Specifications</CardTitle>
          <SpecTable rows={[
            ['Main Bus Voltage','24 V DC','o'],
            ['Max Coil Current','3.0 A (each)','o'],
            ['Logic Supply','3.3 V (RP2040)','g'],
            ['PWM Freq (Coil 1)','100 kHz','a'],
            ['H-bridge Switching (Coil 2)','1–100 Hz (eddy)','a'],
            ['Current Sense Accuracy','±0.1% (INA226)','g'],
            ['Voltage Sense Range','0–30 V','a'],
            ['Board Power Budget','≤ 1.2 W idle','y'],
          ]} />
        </Card>
        <Card>
          <CardTitle color={T.green}>Sequencing</CardTitle>
          <SpecTable rows={[
            ['Trigger Source','Accelerometer (ICM-42688-P)','a'],
            ['Trigger Condition','Zero velocity on Z-axis',null],
            ['Coil 1 ON Duration','100 ms (software timer)','a'],
            ['Inter-coil Delay','20 ms','a'],
            ['Coil 2 ON Duration','Configurable (default 80 ms)','o'],
            ['Coil 2 Switch Rate','Configurable 1–100 Hz','o'],
            ['Cycle Lockout','500 ms min between cycles',null],
            ['Emergency Stop','Hardware pin (GP22)','a'],
          ]} />
        </Card>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:16 }}>
        <Card>
          <CardTitle color={T.green}>Sensors</CardTitle>
          <SpecTable rows={[
            ['Accelerometer','ICM-42688-P SPI','g'],
            ['Mass Position/Vel','VL53L1X ToF I2C','g'],
            ['Current Sense ×2','INA226 I2C','g'],
            ['Voltage Sense','ADC + divider','g'],
            ['Temp Monitor','NCP18XH103 NTC','g'],
          ]} />
        </Card>
        <Card>
          <CardTitle color={T.yellow}>Safety / Watchdog</CardTitle>
          <SpecTable rows={[
            ['External WDT','TPL5010 (18 min timeout)','y'],
            ['WDT Reset','Hard reset via nRESET',null],
            ['OCP (Coil 1)','INA226 alert → FET off','y'],
            ['DRV Fault','DRV8874 nFAULT → Pico IRQ','y'],
            ['Manual Reset','Tact switch + RC filter',null],
            ['Safe Mode Pin','GP15 pulls FETs low',null],
          ]} />
        </Card>
        <Card>
          <CardTitle color={T.red}>LEO Compliance</CardTitle>
          <SpecTable rows={[
            ['PCB Material','Isola 370HR (TG 180°C)',null],
            ['Conformal Coat','Humiseal 1B31 acrylic',null],
            ['Component Temp','-40°C to +85°C (ind.)',null],
            ['Radiation','TID ≤ 10 krad (LEO 2yr)',null],
            ['Mass Target','≤ 55 g PCB + components','a'],
            ['Board Size (est.)','90 × 70 mm','a'],
          ]} />
        </Card>
      </div>
      <WarnBox>
        <strong style={{color:T.yellow}}>Flight vs. Prototype:</strong> Use bare RP2040 IC (not Pico module) for the flight board to save ~3.2 g
        and avoid the onboard USB UART. Replace the Pico internal 3.3V LDO with the external AP2112K on this board.
        All MOSFET gates must have a 10Ω series gate resistor to suppress ringing in the switched coil environment.
      </WarnBox>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  BLOCK DIAGRAM SECTION
// ══════════════════════════════════════════════════════════════════════════════
function BlockDiagSection() {
  return (
    <div>
      <SectionTitle title="System Block Diagram" sub="FUNCTIONAL BLOCKS · SIGNAL FLOWS · POWER DOMAINS" />
      <div style={{ background:'#050810', border:`1px solid ${T.border}`, padding:16, overflowX:'auto' }}>
        <svg viewBox="0 0 900 600" width="100%" xmlns="http://www.w3.org/2000/svg" style={{ fontFamily:T.mono }}>
          <defs>
            <marker id="arr"   markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><polygon points="0 0,8 3,0 6" fill="#4a8fe0" opacity="0.8"/></marker>
            <marker id="arr-o" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><polygon points="0 0,8 3,0 6" fill="#d97706" opacity="0.8"/></marker>
            <marker id="arr-g" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><polygon points="0 0,8 3,0 6" fill="#2eb87e" opacity="0.8"/></marker>
            <marker id="arr-w" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><polygon points="0 0,8 3,0 6" fill="#e8b454" opacity="0.8"/></marker>
          </defs>
          <rect width="900" height="600" fill="#050810"/>
          {/* 24V Input */}
          <rect x="20" y="30" width="110" height="50" fill="#0d1520" stroke="#d97706" strokeWidth="1.5"/>
          <text x="75" y="50" fill="#d97706" fontSize="11" textAnchor="middle" fontWeight="bold">24V IN</text>
          <text x="75" y="64" fill="#445566" fontSize="9" textAnchor="middle">SAT BUS · 3A</text>
          {/* TVS+Fuse */}
          <rect x="155" y="30" width="80" height="50" fill="#0d1520" stroke="#e04a6a" strokeWidth="1"/>
          <text x="195" y="50" fill="#e04a6a" fontSize="10" textAnchor="middle">TVS+FUSE</text>
          <text x="195" y="64" fill="#445566" fontSize="8" textAnchor="middle">SMBJ24A</text>
          <line x1="130" y1="55" x2="153" y2="55" stroke="#d97706" strokeWidth="1.5" markerEnd="url(#arr-o)"/>
          {/* Buck */}
          <rect x="250" y="30" width="90" height="50" fill="#0d1520" stroke="#e8b454" strokeWidth="1.5"/>
          <text x="295" y="48" fill="#e8b454" fontSize="10" textAnchor="middle">BUCK</text>
          <text x="295" y="60" fill="#e8b454" fontSize="9" textAnchor="middle">TPS54302</text>
          <text x="295" y="71" fill="#445566" fontSize="8" textAnchor="middle">24V→5V</text>
          <line x1="235" y1="55" x2="248" y2="55" stroke="#d97706" strokeWidth="1.5" markerEnd="url(#arr-o)"/>
          {/* LDO */}
          <rect x="355" y="30" width="90" height="50" fill="#0d1520" stroke="#2eb87e" strokeWidth="1.5"/>
          <text x="400" y="48" fill="#2eb87e" fontSize="10" textAnchor="middle">LDO 3.3V</text>
          <text x="400" y="60" fill="#2eb87e" fontSize="9" textAnchor="middle">AP2112K</text>
          <text x="400" y="71" fill="#445566" fontSize="8" textAnchor="middle">5V→3.3V 600mA</text>
          <line x1="340" y1="55" x2="353" y2="55" stroke="#e8b454" strokeWidth="1.5" markerEnd="url(#arr-w)"/>
          {/* RP2040 central block */}
          <rect x="330" y="140" width="220" height="200" fill="#0a1525" stroke="#4a8fe0" strokeWidth="2"/>
          <text x="440" y="165" fill="#4a8fe0" fontSize="13" textAnchor="middle" fontWeight="bold">RP2040</text>
          <line x1="340" y1="175" x2="540" y2="175" stroke="#1e2d44" strokeWidth="0.5"/>
          <text x="344" y="192" fill="#445566" fontSize="8">SPI0  → ICM-42688-P IMU</text>
          <text x="344" y="206" fill="#445566" fontSize="8">I2C1  → INA226×2 + VL53L1X</text>
          <text x="344" y="220" fill="#445566" fontSize="8">GP8   → COIL1 PWM GATE</text>
          <text x="344" y="234" fill="#445566" fontSize="8">GP9/10 → DRV8874 IN1/IN2</text>
          <text x="344" y="248" fill="#445566" fontSize="8">GP12  → WDT KICK (TPL5010)</text>
          <text x="344" y="262" fill="#445566" fontSize="8">GP16  → DRV nFAULT IRQ</text>
          <text x="344" y="276" fill="#445566" fontSize="8">GP26  → ADC 24V SENSE</text>
          <text x="344" y="290" fill="#445566" fontSize="8">UART0 → DEBUG / TELEMETRY</text>
          {/* 3.3V to RP2040 */}
          <line x1="400" y1="80" x2="400" y2="138" stroke="#2eb87e" strokeWidth="1.5" markerEnd="url(#arr-g)"/>
          <text x="404" y="115" fill="#2eb87e" fontSize="9">3V3</text>
          {/* WDT */}
          <rect x="590" y="30" width="90" height="50" fill="#0d1520" stroke="#e8b454" strokeWidth="1"/>
          <text x="635" y="50" fill="#e8b454" fontSize="10" textAnchor="middle">WATCHDOG</text>
          <text x="635" y="62" fill="#e8b454" fontSize="9" textAnchor="middle">TPL5010</text>
          <line x1="540" y1="55" x2="588" y2="55" stroke="#2eb87e" strokeWidth="1" strokeDasharray="3,2"/>
          <line x1="635" y1="80" x2="635" y2="200" stroke="#e8b454" strokeWidth="1" strokeDasharray="3,2"/>
          <line x1="635" y1="200" x2="552" y2="200" stroke="#e8b454" strokeWidth="1" markerEnd="url(#arr-w)"/>
          <text x="592" y="197" fill="#e8b454" fontSize="8">nRST</text>
          {/* Coil 1 */}
          <rect x="20" y="220" width="130" height="100" fill="#0d1520" stroke="#4a8fe0" strokeWidth="1.5"/>
          <text x="85" y="242" fill="#4a8fe0" fontSize="11" textAnchor="middle" fontWeight="bold">COIL 1 DRIVER</text>
          <text x="85" y="256" fill="#445566" fontSize="8" textAnchor="middle">IRLZ44N + TC4427A</text>
          <text x="85" y="280" fill="#4a8fe0" fontSize="10" textAnchor="middle">ELECTROMAGNET</text>
          <text x="85" y="294" fill="#445566" fontSize="8" textAnchor="middle">24V / 0–3A PWM</text>
          <line x1="330" y1="220" x2="152" y2="270" stroke="#4a8fe0" strokeWidth="1.5" markerEnd="url(#arr)"/>
          {/* 24V rail */}
          <line x1="130" y1="270" x2="160" y2="270" stroke="#d97706" strokeWidth="2"/>
          <line x1="160" y1="270" x2="160" y2="175" stroke="#d97706" strokeWidth="2"/>
          <line x1="160" y1="175" x2="590" y2="175" stroke="#d97706" strokeWidth="2"/>
          <line x1="590" y1="175" x2="590" y2="200" stroke="#d97706" strokeWidth="2" markerEnd="url(#arr-o)"/>
          <text x="370" y="170" fill="#d97706" fontSize="9" fontWeight="bold">24V POWER RAIL</text>
          {/* Coil 2 */}
          <rect x="590" y="140" width="150" height="110" fill="#0d1520" stroke="#d97706" strokeWidth="1.5"/>
          <text x="665" y="162" fill="#d97706" fontSize="11" textAnchor="middle" fontWeight="bold">COIL 2 H-BRIDGE</text>
          <text x="665" y="176" fill="#445566" fontSize="8" textAnchor="middle">DRV8874PWPR</text>
          <text x="665" y="212" fill="#d97706" fontSize="10" textAnchor="middle">EDDY DAMPER COIL</text>
          <text x="665" y="226" fill="#445566" fontSize="8" textAnchor="middle">DC polarity SWITCH</text>
          <line x1="552" y1="240" x2="588" y2="200" stroke="#d97706" strokeWidth="1.5" markerEnd="url(#arr-o)"/>
          <text x="558" y="230" fill="#d97706" fontSize="8">IN1/2/EN</text>
          <line x1="590" y1="165" x2="554" y2="260" stroke="#e04a6a" strokeWidth="1" strokeDasharray="3,2" markerEnd="url(#arr)"/>
          <text x="555" y="210" fill="#e04a6a" fontSize="8">nFAULT</text>
          {/* Sensors */}
          <rect x="590" y="350" width="150" height="130" fill="#0d1520" stroke="#2eb87e" strokeWidth="1"/>
          <text x="665" y="370" fill="#2eb87e" fontSize="11" textAnchor="middle" fontWeight="bold">SENSORS</text>
          <text x="600" y="388" fill="#445566" fontSize="8">ICM-42688P  SPI  ACCEL</text>
          <text x="600" y="402" fill="#445566" fontSize="8">VL53L1X     I2C  ToF</text>
          <text x="600" y="416" fill="#445566" fontSize="8">INA226×2    I2C  I+V</text>
          <text x="600" y="430" fill="#445566" fontSize="8">NCP18XH103  ADC  TEMP</text>
          <text x="600" y="444" fill="#445566" fontSize="8">Vbus div    ADC  24V</text>
          <text x="600" y="458" fill="#445566" fontSize="8">Status LEDs GPIO OUT</text>
          <line x1="590" y1="415" x2="554" y2="340" stroke="#2eb87e" strokeWidth="1" strokeDasharray="3,2" markerEnd="url(#arr-g)"/>
          {/* INA226 x2 */}
          <rect x="20" y="340" width="130" height="50" fill="#0d1520" stroke="#2eb87e" strokeWidth="1"/>
          <text x="85" y="362" fill="#2eb87e" fontSize="10" textAnchor="middle">INA226 #1</text>
          <text x="85" y="376" fill="#445566" fontSize="8" textAnchor="middle">0x40 · Coil 1</text>
          <line x1="85" y1="320" x2="85" y2="338" stroke="#4a8fe0" strokeWidth="1" markerEnd="url(#arr)"/>
          <rect x="590" y="270" width="150" height="50" fill="#0d1520" stroke="#2eb87e" strokeWidth="1"/>
          <text x="665" y="292" fill="#2eb87e" fontSize="10" textAnchor="middle">INA226 #2</text>
          <text x="665" y="306" fill="#445566" fontSize="8" textAnchor="middle">0x41 · Coil 2</text>
          <line x1="665" y1="250" x2="665" y2="268" stroke="#d97706" strokeWidth="1" markerEnd="url(#arr-o)"/>
          {/* Legend */}
          <rect x="15" y="430" width="280" height="110" fill="none" stroke="#1e2d44" strokeWidth="1"/>
          <text x="25" y="448" fill="#445566" fontSize="8">LEGEND</text>
          <line x1="25" y1="462" x2="65" y2="462" stroke="#4a8fe0" strokeWidth="1.5"/>
          <text x="70" y="466" fill="#445566" fontSize="8">Control / PWM signal</text>
          <line x1="25" y1="477" x2="65" y2="477" stroke="#d97706" strokeWidth="1.5"/>
          <text x="70" y="481" fill="#445566" fontSize="8">24V power / coil drive</text>
          <line x1="25" y1="492" x2="65" y2="492" stroke="#2eb87e" strokeWidth="1.5" strokeDasharray="4,2"/>
          <text x="70" y="496" fill="#445566" fontSize="8">I2C / SPI data bus</text>
          <line x1="25" y1="507" x2="65" y2="507" stroke="#e8b454" strokeWidth="1" strokeDasharray="3,2"/>
          <text x="70" y="511" fill="#445566" fontSize="8">Watchdog / reset</text>
          <line x1="25" y1="522" x2="65" y2="522" stroke="#e04a6a" strokeWidth="1" strokeDasharray="3,2"/>
          <text x="70" y="526" fill="#445566" fontSize="8">Fault / interrupt</text>
          <text x="450" y="588" fill="#1e2d44" fontSize="9" textAnchor="middle">SPACEMANCRAFT · EMMS Coil Driver REV A1 · BLOCK DIAGRAM</text>
        </svg>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginTop:16 }}>
        <Card>
          <CardTitle>Coil 1 — Drive Electromagnet</CardTitle>
          <p style={{ fontSize:13, lineHeight:1.7, color:T.text }}>
            Low-side N-channel MOSFET (IRLZ44N, 55V, 47A, logic-level). Driven via TC4427A dual gate driver (1.5A peak) from Pico GP8 PWM.
            Duty-cycle adjusts coil current 0–3A in response to LiDAR feedback PID. Flyback diode SS34 clamps coil kick-back.
            Series 10Ω gate resistor suppresses EMI. Current measured by INA226 #1 (0x40).
          </p>
        </Card>
        <Card>
          <CardTitle color={T.orange}>Coil 2 — Eddy Current Damper</CardTitle>
          <p style={{ fontSize:13, lineHeight:1.7, color:T.text }}>
            Full H-bridge via DRV8874PWPR (40V, 3A, integrated OCP and thermal shutdown, nFAULT → Pico IRQ).
            DC polarity switches at configurable rate (1–100 Hz default) to produce a time-varying magnetic field inducing
            eddy currents in the conductive mass, damping it. Switching rate adjusted from VL53L1X velocity feedback.
          </p>
        </Card>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  PIN MAP SECTION
// ══════════════════════════════════════════════════════════════════════════════
const PIN_DATA = [
  { group:'SPI0 · ICM-42688-P IMU', color:T.accent },
  { gp:'GP2', pin:'4',  dir:'OUT', func:'SPI0 SCK',        net:'IMU_SCK',   note:'ICM-42688-P SCLK' },
  { gp:'GP3', pin:'5',  dir:'OUT', func:'SPI0 MOSI',       net:'IMU_MOSI',  note:'ICM-42688-P MOSI' },
  { gp:'GP4', pin:'6',  dir:'IN',  func:'SPI0 MISO',       net:'IMU_MISO',  note:'ICM-42688-P MISO' },
  { gp:'GP5', pin:'7',  dir:'IN',  func:'IMU INT1',        net:'IMU_INT',   note:'Zero-motion detect. 10kΩ pull-down.' },
  { gp:'GP6', pin:'9',  dir:'OUT', func:'SPI0 CS (IMU)',   net:'IMU_CS',    note:'Active low. 10kΩ pull-up (idle HIGH).' },
  { group:'I2C1 · Sensors Bus', color:T.accent },
  { gp:'GP14',pin:'19', dir:'I/O', func:'I2C1 SDA',        net:'I2C_SDA',   note:'4.7kΩ pull-up. INA226×2 + VL53L1X.' },
  { gp:'GP15',pin:'20', dir:'OUT', func:'I2C1 SCL',        net:'I2C_SCL',   note:'4.7kΩ pull-up.' },
  { group:'Coil 1 — Drive Electromagnet', color:T.accent },
  { gp:'GP8', pin:'11', dir:'OUT', func:'Coil 1 PWM Gate', net:'COIL1_PWM', note:'→ TC4427A → IRLZ44N gate. 100kHz.' },
  { gp:'GP20',pin:'26', dir:'IN',  func:'INA226 #1 Alert', net:'INA1_ALERT',note:'Active low. OCP >3.3A: cut GP8 PWM.' },
  { group:'Coil 2 — Eddy Damper H-Bridge', color:T.orange },
  { gp:'GP9', pin:'12', dir:'OUT', func:'DRV8874 IN1',     net:'DRV_IN1',   note:'Polarity ctrl A. Toggle with IN2.' },
  { gp:'GP10',pin:'14', dir:'OUT', func:'DRV8874 IN2',     net:'DRV_IN2',   note:'Polarity ctrl B. Complement of IN1.' },
  { gp:'GP11',pin:'15', dir:'OUT', func:'DRV8874 EN/PWM',  net:'DRV_EN',    note:'Enable + power level PWM. 20kHz.' },
  { gp:'GP16',pin:'21', dir:'IN',  func:'DRV8874 nFAULT',  net:'DRV_FAULT', note:'Active low. OCP/thermal → IRQ handler.' },
  { gp:'GP17',pin:'22', dir:'OUT', func:'DRV8874 nSLEEP',  net:'DRV_SLEEP', note:'HIGH=active, LOW=sleep (default).' },
  { gp:'GP21',pin:'27', dir:'IN',  func:'INA226 #2 Alert', net:'INA2_ALERT',note:'OCP on Coil 2. Disable DRV EN.' },
  { group:'Sensors Misc', color:T.green },
  { gp:'GP18',pin:'24', dir:'OUT', func:'VL53L1X XSHUT',   net:'TOF_XSHUT', note:'Active LOW shutdown during power-up.' },
  { gp:'GP19',pin:'25', dir:'IN',  func:'VL53L1X GPIO1',   net:'TOF_IRQ',   note:'Data ready. 10kΩ pull-up.' },
  { group:'Watchdog / Safety', color:T.yellow },
  { gp:'GP12',pin:'16', dir:'OUT', func:'WDT DONE Kick',   net:'WDT_DONE',  note:'Pulse HIGH 1µs. TPL5010 DONE pin.' },
  { gp:'GP13',pin:'17', dir:'OUT', func:'Fault LED (Red)', net:'LED_FAULT',  note:'330Ω series. Active HIGH.' },
  { gp:'GP7', pin:'10', dir:'OUT', func:'Status LED (Grn)',net:'LED_STATUS', note:'330Ω series. Blink = alive.' },
  { gp:'GP22',pin:'29', dir:'IN',  func:'Safe Mode / E-stop',net:'SW_SAFE', note:'10kΩ pull-up. LOW = safe mode or E-stop.' },
  { group:'ADC Inputs', color:T.accent },
  { gp:'GP26',pin:'31', dir:'IN',  func:'ADC0 — 24V Sense',net:'VBUS_SENSE',note:'Divider 100kΩ/10kΩ → 0–3.0V for 0–33V.' },
  { gp:'GP27',pin:'32', dir:'IN',  func:'ADC1 — NTC Temp', net:'TEMP_SENSE',note:'NCP18XH103 on FET heatsink. 10kΩ div.' },
  { gp:'GP28',pin:'34', dir:'IN',  func:'ADC2 — Spare',    net:'ADC_SPARE', note:'Reserved. Test point access.' },
  { group:'UART Debug / Telemetry', color:T.dim },
  { gp:'GP0', pin:'1',  dir:'OUT', func:'UART0 TX',        net:'DBG_TX',    note:'Debug/telemetry. J4 header. 115200 baud.' },
  { gp:'GP1', pin:'2',  dir:'IN',  func:'UART0 RX',        net:'DBG_RX',    note:'Command receive. J4 header.' },
];

function PinMapSection() {
  return (
    <div>
      <SectionTitle title="RP2040 Pin Map" sub="GPIO ASSIGNMENTS · PERIPHERAL MAPPING" />
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${T.border}` }}>
              {['GPIO','Pico Pin','Dir','Function','Net','Notes'].map(h => (
                <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontFamily:T.mono, fontSize:9, letterSpacing:1.5, color:T.dim, background:'#0a1020' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PIN_DATA.map((row, i) => {
              if (row.group) return (
                <tr key={i} style={{ borderBottom:`1px solid ${T.border}10` }}>
                  <td colSpan={6} style={{ padding:'10px 12px 4px', color:row.color, fontFamily:T.mono, fontSize:9, letterSpacing:2 }}>
                    ── {row.group} ──
                  </td>
                </tr>
              );
              return (
                <tr key={i} style={{ borderBottom:`1px solid ${T.border}30` }}
                  onMouseEnter={e => e.currentTarget.style.background = `${T.accent}04`}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding:'6px 10px', fontFamily:T.mono, color:T.accent, fontSize:11 }}>{row.gp}</td>
                  <td style={{ padding:'6px 10px', fontFamily:T.mono, color:T.dim, fontSize:11 }}>{row.pin}</td>
                  <td style={{ padding:'6px 10px', fontFamily:T.mono, fontSize:10,
                    color: row.dir==='OUT' ? T.orange : row.dir==='IN' ? T.green : T.accent }}>{row.dir}</td>
                  <td style={{ padding:'6px 10px', color:T.text, fontSize:13 }}>{row.func}</td>
                  <td style={{ padding:'6px 10px', fontFamily:T.mono, color:T.green, fontSize:11 }}>{row.net}</td>
                  <td style={{ padding:'6px 10px', color:T.dim, fontSize:11 }}>{row.note}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  TIMING SECTION
// ══════════════════════════════════════════════════════════════════════════════
function TimingSection() {
  return (
    <div>
      <SectionTitle title="Timing Diagram" sub="SINGLE CYCLE · ACCELEROMETER TRIGGER → COIL SEQUENCE → FEEDBACK" />
      <div style={{ background:'#050810', border:`1px solid ${T.border}`, padding:16, overflowX:'auto' }}>
        <svg viewBox="0 0 860 380" width="100%" xmlns="http://www.w3.org/2000/svg" style={{ fontFamily:T.mono }}>
          <rect width="860" height="380" fill="#050810"/>
          {[120,230,340,384,494,604,714,824].map(x => (
            <line key={x} x1={x} y1="20" x2={x} y2="340" stroke="#1e2d44" strokeWidth="0.5"/>
          ))}
          <line x1="120" y1="350" x2="840" y2="350" stroke="#1e2d44" strokeWidth="1"/>
          {[[120,'0'],[230,'+50ms'],[340,'+100ms'],[384,'+120ms'],[494,'+170ms'],[604,'+220ms'],[714,'+270ms'],[824,'+320ms']].map(([x,t]) => (
            <text key={x} x={x} y="368" fill="#445566" fontSize="10" textAnchor="middle">{t}</text>
          ))}
          {/* Signal labels */}
          <text x="10" y="60" fill="#4a8fe0" fontSize="9">ACCEL INT1</text>
          <text x="10" y="117" fill="#4a8fe0" fontSize="9">COIL1 PWM</text>
          <text x="10" y="177" fill="#d97706" fontSize="9">COIL2 EN</text>
          <text x="10" y="235" fill="#d97706" fontSize="9">COIL2 IN1/2</text>
          <text x="10" y="290" fill="#2eb87e" fontSize="9">ToF ACTIVE</text>
          <text x="10" y="340" fill="#e8b454" fontSize="9">WDT KICK</text>
          {/* ACCEL INT1 pulse at t=0 */}
          <polyline points="120,75 120,50 128,50 128,75 840,75" fill="none" stroke="#4a8fe0" strokeWidth="1.5"/>
          <text x="129" y="46" fill="#4a8fe0" fontSize="9">Zero-vel detect</text>
          {/* COIL1 ON 0→100ms */}
          <rect x="120" y="110" width="220" height="20" fill="rgba(74,143,224,0.12)"/>
          <polyline points="120,130 120,110 340,110 340,130 840,130" fill="none" stroke="#4a8fe0" strokeWidth="2"/>
          <text x="225" y="108" fill="#4a8fe0" fontSize="9" textAnchor="middle">100ms</text>
          {/* 20ms gap */}
          <line x1="340" y1="95" x2="384" y2="95" stroke="#445566" strokeWidth="1" strokeDasharray="3,2"/>
          <text x="362" y="92" fill="#445566" fontSize="8" textAnchor="middle">20ms</text>
          {/* COIL2 EN 120→220ms */}
          <rect x="384" y="170" width="220" height="20" fill="rgba(217,119,6,0.12)"/>
          <polyline points="120,190 384,190 384,170 604,170 604,190 840,190" fill="none" stroke="#d97706" strokeWidth="2"/>
          <text x="494" y="168" fill="#d97706" fontSize="9" textAnchor="middle">80ms</text>
          {/* COIL2 polarity switching */}
          <polyline points={`120,250 384,250 384,230 400,230 400,250 416,250 416,230 432,230 432,250 448,250 448,230 464,230 464,250 480,250 480,230 496,230 496,250 512,250 512,230 528,230 528,250 544,250 544,230 560,230 560,250 576,250 576,230 592,230 592,250 604,250 840,250`} fill="none" stroke="#d97706" strokeWidth="1.5"/>
          {/* ToF continuous */}
          <rect x="340" y="283" width="284" height="24" fill="rgba(46,184,126,0.1)"/>
          <line x1="120" y1="285" x2="840" y2="285" stroke="#2eb87e" strokeWidth="1" strokeDasharray="2,4" opacity="0.5"/>
          <polyline points="120,305 840,305" fill="none" stroke="#2eb87e" strokeWidth="1.5"/>
          {/* WDT kicks */}
          {[227,337,447,557,667].map(x => <rect key={x} x={x} y="340" width="4" height="10" fill="#e8b454" opacity="0.8"/>)}
          <polyline points="120,345 840,345" fill="none" stroke="#e8b454" strokeWidth="0.5" opacity="0.3"/>
          {/* Vertical markers */}
          {[120,340,384,604].map((x,i) => (
            <line key={x} x1={x} y1="20" x2={x} y2="350" stroke={i<2?'#4a8fe0':'#d97706'} strokeWidth="0.8" strokeDasharray="4,4" opacity="0.5"/>
          ))}
          {[['TRIGGER',120,'#4a8fe0'],['COIL1 OFF',340,'#4a8fe0'],['COIL2 ON',384,'#d97706'],['COIL2 OFF',604,'#d97706']].map(([l,x,c]) => (
            <text key={x} x={x} y="18" fill={c} fontSize="9" textAnchor="middle">{l}</text>
          ))}
          <line x1="604" y1="348" x2="824" y2="348" stroke="#445566" strokeWidth="1"/>
          <text x="714" y="344" fill="#445566" fontSize="8" textAnchor="middle">≥ 500ms lockout</text>
        </svg>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginTop:16 }}>
        <Card>
          <CardTitle>Timing Parameters (Configurable)</CardTitle>
          <SpecTable rows={[
            ['t_coil1_on','100 ms (software, ±1ms)','a'],
            ['t_delay','20 ms (software)','a'],
            ['t_coil2_on','80 ms default, 20–500ms range','o'],
            ['f_eddy_switch','5 Hz default, 1–100 Hz range','o'],
            ['t_lockout','500 ms minimum between cycles',null],
            ['t_tof_burst','100ms window, 20ms/sample','g'],
            ['t_wdt_kick','Every ~50ms (10× margin on 60s WDT)','y'],
          ]} />
        </Card>
        <Card>
          <CardTitle color={T.orange}>PID Control Loop</CardTitle>
          <p style={{ fontSize:13, lineHeight:1.6, color:T.text }}>
            Coil 1 duty and Coil 2 EN duty are adjusted each cycle from LiDAR feedback.
            VL53L1X reports position every 20ms; velocity = Δdist/Δtime.
            If velocity &lt; target → increase duty. If mass overshoots → reduce next-cycle power.
            INA226 provides real-time current to ensure we stay under 3A.
            Eddy switching frequency is also tunable — higher f_eddy = stronger damping.
          </p>
        </Card>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  FIRMWARE SECTION
// ══════════════════════════════════════════════════════════════════════════════
const FW_CODE = `// EMMS COIL DRIVER FIRMWARE — RP2040 C SDK (Pseudocode outline)
// Spacemancraft Pvt Ltd · EP-ADCS · Rev A1

typedef struct {
    float   coil1_duty;        // 0.0 - 1.0 (PWM duty)
    float   coil2_duty;        // 0.0 - 1.0 (EN PWM duty)
    float   eddy_freq_hz;      // 1-100 Hz polarity switch
    float   mass_pos_mm;       // from VL53L1X
    float   mass_vel_mm_s;     // computed: Δpos/Δt
    float   coil1_current_A;   // from INA226 #1
    float   coil2_current_A;   // from INA226 #2
    bool    fault_active;
    uint32_t last_cycle_ms;
} EMSSState;

int main() {
    init_peripherals();        // clocks, SPI, I2C, PWM, ADC
    init_sensors();            // IMU, VL53L1X, INA226×2
    init_watchdog_kick();      // repeating timer every 50ms
    drv8874_sleep(true);       // DRV8874 starts in sleep
    coil1_pwm_set(0.0f);       // Gate LOW

    gpio_set_irq_enabled_with_callback(
        IMU_INT_PIN, GPIO_IRQ_EDGE_RISE, true, &on_zero_motion_detect
    );

    while (true) {
        read_tof_if_ready();   // non-blocking, uses GPIO1 IRQ
        read_ina226_both();    // I2C read V+I for both coils
        read_bus_voltage_adc();
        check_faults();        // nFAULT, OCP alerts → safe mode
        telemetry_send();      // UART0 packet every 100ms
        tight_loop_contents(); // RP2040 idle power reduction
    }
}

void on_zero_motion_detect() {
    uint32_t now = to_ms_since_boot(get_absolute_time());
    if (now - state.last_cycle_ms < 500) return; // lockout
    if (state.fault_active) return;
    state.last_cycle_ms = now;
    run_coil_sequence();
}

void run_coil_sequence() {
    float c1_duty = pid_compute_coil1(state.mass_pos_mm, state.mass_vel_mm_s);
    float c2_duty = pid_compute_coil2(state.mass_vel_mm_s);

    // Step 1: Fire Coil 1 — 100ms
    coil1_pwm_set(c1_duty);
    tof_set_high_rate(true);
    sleep_ms(100);             // use hardware_alarm in flight
    coil1_pwm_set(0.0f);

    // Step 2: 20ms inter-coil delay
    sleep_ms(20);

    // Step 3: Fire Coil 2 (H-bridge eddy damper)
    drv8874_sleep(false);
    drv8874_set_pwm(c2_duty);
    eddy_polarity_switch_start(state.eddy_freq_hz); // PIO program
    sleep_ms(80);
    eddy_polarity_switch_stop();
    drv8874_sleep(true);
    tof_set_high_rate(false);

    pid_update(state.mass_pos_mm, state.mass_vel_mm_s);
}

bool wdt_kick_callback(struct repeating_timer *t) {
    gpio_put(WDT_DONE_PIN, 1);
    busy_wait_us(2);
    gpio_put(WDT_DONE_PIN, 0);
    return true; // keep repeating
}

void check_faults() {
    if (!gpio_get(DRV_FAULT_PIN)) {
        coil1_pwm_set(0.0f);
        drv8874_sleep(true);
        state.fault_active = true;
        gpio_put(LED_FAULT_PIN, 1);
        telemetry_fault_packet();
    }
    if (!gpio_get(INA1_ALERT_PIN)) {
        coil1_pwm_set(0.0f);
        state.coil1_duty *= 0.8f; // back off 20%
    }
}`;

function FirmwareSection() {
  return (
    <div>
      <SectionTitle title="Firmware Logic" sub="RP2040 C SDK · CONTROL FLOW · KEY ROUTINES" />
      <InfoBox>
        Recommended: <strong style={{color:T.accent}}>C SDK</strong> for deterministic timing on coil sequence (hardware alarms, not busy-wait).
        Prototype in MicroPython first. Critical timing: use <code style={{fontFamily:T.mono,background:'#0a1525',padding:'1px 4px'}}>hardware_alarm</code> API.
        All peripheral init must occur before enabling coil drivers. Watchdog must be kicked every loop.
      </InfoBox>
      <div style={{ background:'#050810', border:`1px solid ${T.border}`, borderLeft:`3px solid ${T.accent}`,
        padding:20, fontFamily:T.mono, fontSize:11, lineHeight:1.8, overflowX:'auto', whiteSpace:'pre', color:T.dim }}>
        {FW_CODE}
      </div>
      <div style={{ marginTop:16 }}>
        <Card>
          <CardTitle color={T.green}>Telemetry Packet Format (UART0, 115200 baud)</CardTitle>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                  {['Byte(s)','Field','Type','Range','Notes'].map(h => (
                    <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontFamily:T.mono, fontSize:9, letterSpacing:1.5, color:T.dim, background:'#0a1020' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['0-1','Sync','uint16','0xAA55','Frame start marker'],
                  ['2-5','Timestamp','uint32','ms since boot','Milliseconds since power-on'],
                  ['6-9','Mass Position','float32','0–4000 mm','VL53L1X range reading'],
                  ['10-13','Mass Velocity','float32','±5000 mm/s','Computed Δpos/Δt'],
                  ['14-17','Coil1 Current','float32','0–3.5 A','INA226 #1'],
                  ['18-21','Coil1 Voltage','float32','0–26 V','INA226 #1 bus'],
                  ['22-25','Coil2 Current','float32','0–3.5 A','INA226 #2'],
                  ['26-29','Coil2 Voltage','float32','0–26 V','INA226 #2 bus'],
                  ['30-33','Bus Voltage','float32','0–30 V','ADC GP26'],
                  ['34','Flags','uint8','—','Bit0=fault, Bit1=coil1_on, Bit2=coil2_on, Bit3=safe'],
                  ['35-36','CRC16','uint16','—','CRC-CCITT over bytes 0–34'],
                ].map(([b,f,t,r,n]) => (
                  <tr key={b} style={{ borderBottom:`1px solid ${T.border}30` }}>
                    <td style={{ padding:'7px 10px', fontFamily:T.mono, fontSize:11, color:T.accent }}>{b}</td>
                    <td style={{ padding:'7px 10px', color:T.text }}>{f}</td>
                    <td style={{ padding:'7px 10px', fontFamily:T.mono, fontSize:11, color:T.green }}>{t}</td>
                    <td style={{ padding:'7px 10px', fontFamily:T.mono, fontSize:11, color:T.dim }}>{r}</td>
                    <td style={{ padding:'7px 10px', color:T.dim, fontSize:11 }}>{n}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  PCB NOTES SECTION
// ══════════════════════════════════════════════════════════════════════════════
function PCBNotesSection() {
  return (
    <div>
      <SectionTitle title="PCB Layout Notes" sub="STACKUP · ROUTING RULES · SPACE DESIGN CONSIDERATIONS" />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <Card>
          <CardTitle color={T.yellow}>Recommended Stackup (4-layer)</CardTitle>
          <SpecTable rows={[
            ['L1 — Top','Components + Signal','a'],
            ['L2 — Inner 1','Solid GND Plane','y'],
            ['L3 — Inner 2','24V Power Plane','o'],
            ['L4 — Bottom','Signal + 3.3V fills','g'],
            ['Material','Isola 370HR (TG 180°C)',null],
            ['Copper weight','2 oz outer, 1 oz inner',null],
            ['Board thickness','1.0 mm (lightweight)',null],
            ['Finish','ENIG (corrosion-resistant)',null],
            ['Solder mask','Black LPI (thermal)',null],
            ['Conformal coat','Humiseal 1B31 acrylic',null],
          ]} />
        </Card>
        <Card>
          <CardTitle color={T.red}>Critical Routing Rules</CardTitle>
          <SpecTable rows={[
            ['24V coil traces','≥ 2.5mm width (3A)','o'],
            ['Signal traces','0.15mm min',null],
            ['I2C SDA/SCL','Keep <50mm, matched',null],
            ['Gate drive traces','Short, direct, 0.3mm',null],
            ['Flyback diode D1','Place within 3mm of coil pads','y'],
            ['INA226 shunt','Kelvin sense traces',null],
            ['Buck SW node','Minimize copper area',null],
            ['Crystal Y1','Keep <5mm from RP2040',null],
            ['Pour','GND pour on L1+L4',null],
            ['Via stitching','Every 5mm on GND edge',null],
          ]} />
        </Card>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:16 }}>
        <Card>
          <CardTitle>Thermal Management</CardTitle>
          <p style={{ fontSize:13, lineHeight:1.6, color:T.text }}>
            IRLZ44N TO-220: clip-on heatsink (1.5°C/W). At 200mW → ΔT ≈ 30°C above ambient.
            DRV8874 HTSSOP-16: thermal pad to L2 GND via array (4×4 under package).
            TPS54302: output cap + L1 close to feedback divider.
          </p>
        </Card>
        <Card>
          <CardTitle color={T.green}>EMC / Isolation</CardTitle>
          <p style={{ fontSize:13, lineHeight:1.6, color:T.text }}>
            Keep 24V coil switching region physically separated from ICM-42688-P sensor.
            Moat/slot between high-current region and analog sensor region.
            Gate drive loops minimized. Ground shield traces around SPI to IMU.
            Optional ferrite bead (600Ω @ 100MHz) on 3.3V to sensor rail.
          </p>
        </Card>
        <Card>
          <CardTitle color={T.red}>Space Qualification</CardTitle>
          <p style={{ fontSize:13, lineHeight:1.6, color:T.text }}>
            Avoid tantalum caps (combustion risk in TVAC). Use X7R MLCC + low-ESR aluminum.
            DNP J4 debug header for flight. Add test points on all nets.
            Adhesive for SMD components (launch vibration >100g rms).
            100% no-clean flux, ultrasonic clean after reflow.
            Conformal coat after test — mask connectors, leave coil pads uncoated.
          </p>
        </Card>
      </div>
      <WarnBox>
        <strong style={{color:T.yellow}}>Next Steps:</strong> (1) Capture schematic in KiCad 7 using the above netlist.
        (2) Order bare RP2040, DRV8874, INA226, ICM-42688-P from Mouser/Digi-Key.
        (3) 4-layer JLCPCB/PCBWay order with Isola 370HR.
        (4) Bringup: power first (measure 5V, 3.3V) → RP2040 boot → sensors ping → coil driver bench test with 8Ω 25W resistive load.
        (5) Calibrate VL53L1X zero-point with mass at rest. (6) Tune PID in flatsat before TVAC.
      </WarnBox>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  SCHEMATIC NOTES SECTION
// ══════════════════════════════════════════════════════════════════════════════
function SchematicSection() {
  const sections = [
    {
      title:'Section 1 — Power Supply', color:T.orange,
      left:(
        <>
          <p style={{fontSize:13,lineHeight:1.7,marginBottom:10}}>
            <span style={{color:T.orange,fontFamily:T.mono}}>24V IN →</span> Polyfuse MF-MSMF300 (3A hold / 6A trip) → TVS SMBJ24A clamps to 38.9V.
            Bulk cap 100µF/50V electrolytic (Panasonic FK) + 10µF MLCC.
          </p>
          <p style={{fontSize:13,lineHeight:1.7,marginBottom:10}}>
            <span style={{color:T.yellow,fontFamily:T.mono}}>24V→5V:</span> TPS54302DDCT sync buck. 570kHz. L = 10µH Bourns SRR1260. Output cap 47µF/10V.
          </p>
          <p style={{fontSize:13,lineHeight:1.7}}>
            <span style={{color:T.green,fontFamily:T.mono}}>5V→3.3V:</span> AP2112K-3.3TRG1 LDO (600mA, low noise). Feeds RP2040, all I2C sensors, ICM-42688-P. 100nF input + 1µF output.
          </p>
        </>
      ),
      rows:[
        ['Input Fuse','MF-MSMF300 (3A PTC)','y'],
        ['TVS','SMBJ24A (38.9V clamp)','y'],
        ['Bulk Cap C1','100µF/50V, FK series',null],
        ['Buck IC','TPS54302DDCT','a'],
        ['Buck L1','10µH Bourns SRR1260',null],
        ['LDO IC','AP2112K-3.3TRG1','g'],
      ],
    },
    {
      title:'Section 2 — Coil 1 Low-Side Driver', color:T.accent,
      left:(
        <>
          <p style={{fontSize:13,lineHeight:1.7,marginBottom:10}}>
            RP2040 GP8 → 10Ω gate resistor R1 → TC4427A Gate Driver. TC4427A sources/sinks 1.5A peak.
            IRLZ44N: Vgs(th)=1–2V, Rds(on)=22mΩ → 200mW conduction loss at 3A.
          </p>
          <p style={{fontSize:13,lineHeight:1.7}}>
            Flyback diode SS34 placed within 3mm of coil connector pads. Add 100nF ceramic across FET D-S as snubber if ringing observed.
            1mΩ shunt (WSL2010) on high side → INA226 differential sense. Alert pin at 3.3A → GP20.
          </p>
        </>
      ),
      rows:[
        ['MOSFET Q1','IRLZ44NPBF (TO-220)','a'],
        ['Gate Driver U2','TC4427ACOA713',null],
        ['Gate Resistor R1','10Ω 0402 1%',null],
        ['Flyback D1','SS34 (Schottky 3A 40V)','y'],
        ['Current Shunt R2','1mΩ Vishay WSL2010','g'],
        ['Current Sense U3','INA226AIDT (I2C 0x40)','g'],
      ],
    },
    {
      title:'Section 3 — Coil 2 H-Bridge (Eddy Damper)', color:T.orange,
      left:(
        <>
          <p style={{fontSize:13,lineHeight:1.7,marginBottom:10}}>
            DRV8874PWPR single H-bridge (HTSSOP-16). VBAT = 24V. Control: GP9→IN1, GP10→IN2, GP11→EN/PWM.
            Polarity switch: IN1=1,IN2=0 → forward; IN1=0,IN2=1 → reverse.
          </p>
          <p style={{fontSize:13,lineHeight:1.7}}>
            PWM on EN pin adjusts average current. VCP cap: 100nF close to pin. nFAULT → 10kΩ pull-up → GP16.
            nSLEEP → GP17 (LOW default = sleep). External cap on ISET pin sets OCP response time.
          </p>
        </>
      ),
      rows:[
        ['H-Bridge IC U4','DRV8874PWPR','o'],
        ['VBAT Cap','100µF/50V + 100nF',null],
        ['VCP Cap C5','100nF 0402 ceramic',null],
        ['nFAULT pull-up','10kΩ to 3.3V',null],
        ['OCP trip point','3A (ISET pin)','y'],
        ['Thermal shutdown','165°C (internal, DRV)','y'],
      ],
    },
  ];

  return (
    <div>
      <SectionTitle title="Schematic Notes" sub="SECTION-BY-SECTION CIRCUIT DESCRIPTION" />
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
        {sections.map(s => (
          <Card key={s.title}>
            <CardTitle color={s.color}>{s.title}</CardTitle>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
              <div>{s.left}</div>
              <SpecTable rows={s.rows} />
            </div>
          </Card>
        ))}
        <Card>
          <CardTitle color={T.green}>Section 4 — Sensors</CardTitle>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
            {[
              { title:'ICM-42688-P (Accelerometer)', body:'SPI mode 0. CS→GP6. SCK→GP2. MOSI→GP3. MISO→GP4. INT1→GP5. Zero-velocity via APEX ZMD: threshold=2 mg, duration=50ms → fires INT1. 100nF+100nF on VDDIO/VDD.' },
              { title:'VL53L1X (ToF LiDAR)', body:'I2C 0x29. SCL/SDA → I2C1 (GP14/15). XSHUT→GP18. GPIO1→GP19. Long range mode, 20ms timing budget. Velocity = Δd/Δt. 100nF on AVDD/IOVDD.' },
              { title:'INA226 ×2', body:'I2C1. #1 at 0x40 (A0=GND,A1=GND) Coil 1. #2 at 0x41 (A0=VS,A1=GND) Coil 2. Shunt 1mΩ → 3A→3mV. Resolution 2.5µV/bit=2.5mA/bit. Alert at 3.3A AND 26V.' },
            ].map(s => (
              <div key={s.title}>
                <div style={{ fontFamily:T.mono, fontSize:10, color:T.green, marginBottom:6 }}>{s.title}</div>
                <p style={{ fontSize:12, lineHeight:1.6, color:T.text }}>{s.body}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  INVENTORY MANAGEMENT SECTION
// ══════════════════════════════════════════════════════════════════════════════
const INV_STATUS = {
  inhouse:  { label:'In House',     color:'#2eb87e', bg:'rgba(46,184,126,0.1)',  border:'rgba(46,184,126,0.3)'  },
  ordered:  { label:'PO Ordered',   color:'#4a8fe0', bg:'rgba(74,143,224,0.1)', border:'rgba(74,143,224,0.3)'  },
  paid:     { label:'Payment Made', color:'#e8b454', bg:'rgba(232,180,84,0.1)', border:'rgba(232,180,84,0.3)'  },
  needed:   { label:'Need to Order',color:'#e04a6a', bg:'rgba(224,74,106,0.1)', border:'rgba(224,74,106,0.3)'  },
  na:       { label:'N/A',          color:'#445566', bg:'rgba(68,85,102,0.1)',  border:'rgba(68,85,102,0.2)'   },
};

function InvStatusBadge({ status }) {
  const m = INV_STATUS[status] || INV_STATUS.na;
  return (
    <span style={{ display:'inline-block', padding:'2px 8px', fontSize:10, fontFamily:T.mono,
      background:m.bg, color:m.color, border:`1px solid ${m.border}`, borderRadius:2, whiteSpace:'nowrap' }}>
      {m.label}
    </span>
  );
}

function InventorySection({ userName }) {
  const [items, setItems]       = useState([]);
  const [saving, setSaving]     = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [filter, setFilter]     = useState('all');
  const [showAddPO, setShowAddPO] = useState(null); // ref being edited for PO
  const [updates, setUpdates]   = useState([]);     // recent docs uploads (for linking)
  const saveTimer = useRef(null);

  // Bootstrap inventory from BOM if empty
  useEffect(() => {
    const db = getDB(); if (!db) return;
    const unsub = onSnapshot(doc(db, 'coil_driver', 'inventory'), snap => {
      if (snap.exists() && snap.data().items) {
        setItems(snap.data().items);
        setLastSaved(snap.data().updatedAt?.toDate?.() || null);
      } else {
        // First load — seed from BOM
        const seed = INITIAL_BOM.map(b => ({
          ref:       b.ref,
          desc:      b.desc,
          pn:        b.pn,
          qty_needed: b.qty,
          qty_inhouse: 0,
          status:    'needed',
          vendor:    '',
          po_number: '',
          po_date:   '',
          paid_date: '',
          amount:    '',
          notes:     '',
          validatedBy: '',
          linkedUpdate: '',
        }));
        setItems(seed);
        saveItems(seed);
      }
    });
    // Also listen to recent uploads for linking
    const unsub2 = onSnapshot(
      query(collection(db, 'coil_driver_updates'), orderBy('uploadedAt','desc')),
      snap => setUpdates(snap.docs.map(d => ({ id:d.id, ...d.data() })).slice(0,30))
    );
    return () => { unsub(); unsub2(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function saveItems(rows) {
    const db = getDB(); if (!db) return;
    setSaving(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await setDoc(doc(db, 'coil_driver', 'inventory'), {
          items: rows, updatedAt: serverTimestamp(), updatedBy: userName || 'embedded',
        });
        setLastSaved(new Date());
      } catch(e) { console.error('Inventory save failed:', e); }
      finally { setSaving(false); }
    }, 800);
  }

  function updateItem(idx, field, value) {
    const next = items.map((r,i) => i===idx ? { ...r, [field]: value } : r);
    setItems(next);
    saveItems(next);
  }

  const filtered = filter === 'all' ? items : items.filter(i => i.status === filter);

  const counts = Object.keys(INV_STATUS).reduce((acc, k) => {
    acc[k] = items.filter(i => i.status === k).length;
    return acc;
  }, {});

  const totalSpend = items
    .filter(i => i.amount)
    .reduce((s, i) => s + parseFloat(i.amount.replace(/[^0-9.]/g,'') || 0), 0);

  return (
    <div>
      <SectionTitle title="Inventory Management" sub="IN-HOUSE · PO ORDERED · PAYMENT MADE · LINKED TO DAILY UPDATES" />

      {/* Summary cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10, marginBottom:16 }}>
        {Object.entries(INV_STATUS).map(([k,m]) => (
          <div key={k} onClick={() => setFilter(filter===k ? 'all' : k)}
            style={{ background: filter===k ? m.bg : T.panel, border:`1px solid ${filter===k ? m.border : T.border}`,
              padding:'12px 10px', cursor:'pointer', textAlign:'center', transition:'all 0.15s' }}>
            <div style={{ fontFamily:T.mono, fontSize:22, fontWeight:700, color:m.color }}>{counts[k]||0}</div>
            <div style={{ fontFamily:T.mono, fontSize:9, color:T.dim, marginTop:4, letterSpacing:1 }}>{m.label.toUpperCase()}</div>
          </div>
        ))}
      </div>

      {/* Spend summary */}
      <div style={{ display:'flex', gap:16, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ background:T.panel, border:`1px solid ${T.border}`, padding:'8px 16px', display:'flex', gap:12, alignItems:'center' }}>
          <span style={{ fontFamily:T.mono, fontSize:10, color:T.dim }}>TOTAL SPEND TRACKED</span>
          <span style={{ fontFamily:T.mono, fontSize:18, fontWeight:700, color:T.yellow }}>
            ₹{totalSpend.toFixed(0)}
          </span>
        </div>
        <div style={{ fontFamily:T.mono, fontSize:10, color:T.dim, marginLeft:'auto' }}>
          {saving && <span style={{ color:T.yellow }}>saving…</span>}
          {!saving && lastSaved && <span style={{ color:T.green }}>saved {new Date(lastSaved).toLocaleTimeString()}</span>}
        </div>
      </div>

      {/* Inventory table */}
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${T.border}` }}>
              {['Ref','Description','P/N','Qty Needed','Qty In-House','Status','Vendor','PO #','PO Date','Paid Date','Amount (₹)','Notes','Linked Update'].map(h => (
                <th key={h} style={{ padding:'7px 8px', textAlign:'left', fontFamily:T.mono, fontSize:8, letterSpacing:1, color:T.dim, background:'#0a1020', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, idx) => {
              const realIdx = items.indexOf(row);
              return (
                <tr key={row.ref} style={{ borderBottom:`1px solid ${T.border}20` }}
                  onMouseEnter={e => e.currentTarget.style.background = `${T.accent}04`}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding:'6px 8px', fontFamily:T.mono, fontSize:10, color:T.accent, whiteSpace:'nowrap' }}>{row.ref}</td>
                  <td style={{ padding:'6px 8px', color:T.text, fontSize:11, maxWidth:160 }}>{row.desc}</td>
                  <td style={{ padding:'6px 8px', fontFamily:T.mono, fontSize:10, color:T.green }}>
                    <EditableCell value={row.pn} onSave={v => updateItem(realIdx,'pn',v)} mono />
                  </td>
                  <td style={{ padding:'6px 8px', textAlign:'center', fontFamily:T.mono, fontSize:11, color:T.dim }}>{row.qty_needed}</td>
                  <td style={{ padding:'6px 8px', textAlign:'center' }}>
                    <EditableCell value={String(row.qty_inhouse||0)} onSave={v => updateItem(realIdx,'qty_inhouse',isNaN(v)?0:Number(v))} mono />
                  </td>
                  <td style={{ padding:'6px 8px' }}>
                    <select value={row.status} onChange={e => updateItem(realIdx,'status',e.target.value)}
                      style={{ background:T.panel2, border:`1px solid ${T.border}`, color:INV_STATUS[row.status]?.color||T.text,
                        fontFamily:T.mono, fontSize:10, padding:'3px 6px' }}>
                      {Object.entries(INV_STATUS).map(([k,m]) => (
                        <option key={k} value={k}>{m.label}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding:'6px 8px' }}>
                    <EditableCell value={row.vendor||''} onSave={v => updateItem(realIdx,'vendor',v)} extraStyle={{ color:T.text, fontSize:11 }} />
                  </td>
                  <td style={{ padding:'6px 8px', fontFamily:T.mono, fontSize:10 }}>
                    <EditableCell value={row.po_number||''} onSave={v => updateItem(realIdx,'po_number',v)} mono />
                  </td>
                  <td style={{ padding:'6px 8px', fontFamily:T.mono, fontSize:10, color:T.dim }}>
                    <EditableCell value={row.po_date||''} onSave={v => updateItem(realIdx,'po_date',v)} mono />
                  </td>
                  <td style={{ padding:'6px 8px', fontFamily:T.mono, fontSize:10, color:T.dim }}>
                    <EditableCell value={row.paid_date||''} onSave={v => updateItem(realIdx,'paid_date',v)} mono />
                  </td>
                  <td style={{ padding:'6px 8px', fontFamily:T.mono, fontSize:10, color:T.yellow }}>
                    <EditableCell value={row.amount||''} onSave={v => updateItem(realIdx,'amount',v)} mono />
                  </td>
                  <td style={{ padding:'6px 8px', color:T.dim, fontSize:11, maxWidth:150 }}>
                    <EditableCell value={row.notes||''} onSave={v => updateItem(realIdx,'notes',v)} />
                  </td>
                  <td style={{ padding:'6px 8px' }}>
                    <select value={row.linkedUpdate||''} onChange={e => updateItem(realIdx,'linkedUpdate',e.target.value)}
                      style={{ background:T.panel2, border:`1px solid ${T.border}`, color:T.dim,
                        fontFamily:T.mono, fontSize:9, padding:'2px 5px', maxWidth:120 }}>
                      <option value=''>—</option>
                      {updates.map(u => (
                        <option key={u.id} value={u.id}>{u.title?.slice(0,30)||'untitled'}</option>
                      ))}
                    </select>
                    {row.linkedUpdate && (
                      <div style={{ fontFamily:T.mono, fontSize:8, color:T.green, marginTop:2 }}>
                        ✓ linked to update
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop:12, fontFamily:T.mono, fontSize:10, color:T.dim }}>
        Double-click any cell to edit · Status updates auto-save · Link items to daily documentation updates
      </div>

      {/* PO / Payment summary */}
      {items.filter(i => i.po_number).length > 0 && (
        <div style={{ marginTop:20 }}>
          <Card>
            <CardTitle color={T.yellow}>Purchase Orders & Payments</CardTitle>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                    {['Component','Vendor','PO Number','PO Date','Payment Date','Amount','Status'].map(h => (
                      <th key={h} style={{ padding:'7px 10px', fontFamily:T.mono, fontSize:9, letterSpacing:1, color:T.dim, background:'#0a1020' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.filter(i => i.po_number).map((item, i) => (
                    <tr key={i} style={{ borderBottom:`1px solid ${T.border}30` }}>
                      <td style={{ padding:'7px 10px', fontFamily:T.mono, fontSize:10, color:T.accent }}>{item.ref}</td>
                      <td style={{ padding:'7px 10px', color:T.text }}>{item.vendor||'—'}</td>
                      <td style={{ padding:'7px 10px', fontFamily:T.mono, fontSize:11, color:T.accent }}>{item.po_number}</td>
                      <td style={{ padding:'7px 10px', fontFamily:T.mono, fontSize:10, color:T.dim }}>{item.po_date||'—'}</td>
                      <td style={{ padding:'7px 10px', fontFamily:T.mono, fontSize:10, color:T.dim }}>{item.paid_date||'—'}</td>
                      <td style={{ padding:'7px 10px', fontFamily:T.mono, fontSize:11, color:T.yellow }}>{item.amount ? `₹${item.amount}` : '—'}</td>
                      <td style={{ padding:'7px 10px' }}><InvStatusBadge status={item.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  VALIDATED SPECS — primary page shows validated/confirmed numbers
//  Engineers upload daily updates → mark as validated → values promoted here
// ══════════════════════════════════════════════════════════════════════════════
const DEFAULT_VALIDATED_SPECS = {
  coil1_duration_ms:   { value:'100', unit:'ms',  label:'Coil 1 ON Duration',     validated:false, note:'', source:'' },
  inter_delay_ms:      { value:'20',  unit:'ms',  label:'Inter-coil Delay',        validated:false, note:'', source:'' },
  coil2_duration_ms:   { value:'80',  unit:'ms',  label:'Coil 2 ON Duration',      validated:false, note:'', source:'' },
  eddy_freq_hz:        { value:'5',   unit:'Hz',  label:'Eddy Switch Frequency',   validated:false, note:'', source:'' },
  coil1_current_A:     { value:'3.0', unit:'A',   label:'Max Coil 1 Current',      validated:false, note:'', source:'' },
  coil2_current_A:     { value:'3.0', unit:'A',   label:'Max Coil 2 Current',      validated:false, note:'', source:'' },
  board_mass_g:        { value:'55',  unit:'g',   label:'Board Mass (target)',      validated:false, note:'', source:'' },
  pwm_freq_khz:        { value:'100', unit:'kHz', label:'Coil 1 PWM Frequency',    validated:false, note:'', source:'' },
  lockout_ms:          { value:'500', unit:'ms',  label:'Cycle Lockout',           validated:false, note:'', source:'' },
  bus_voltage_V:       { value:'24',  unit:'V',   label:'Bus Voltage',             validated:false, note:'', source:'' },
};

function ValidatedSpecsSection({ userName }) {
  const [specs, setSpecs]       = useState(DEFAULT_VALIDATED_SPECS);
  const [updates, setUpdates]   = useState([]);
  const [saving, setSaving]     = useState(false);
  const [editKey, setEditKey]   = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const saveTimer = useRef(null);

  useEffect(() => {
    const db = getDB(); if (!db) return;
    const unsub = onSnapshot(doc(db, 'coil_driver', 'validated_specs'), snap => {
      if (snap.exists() && snap.data().specs) setSpecs({ ...DEFAULT_VALIDATED_SPECS, ...snap.data().specs });
    });
    const unsub2 = onSnapshot(
      query(collection(db, 'coil_driver_updates'), orderBy('uploadedAt','desc')),
      snap => setUpdates(snap.docs.map(d => ({ id:d.id, ...d.data() })).slice(0,20))
    );
    return () => { unsub(); unsub2(); };
  }, []);

  function saveSpecs(next) {
    const db = getDB(); if (!db) return;
    setSaving(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await setDoc(doc(db, 'coil_driver', 'validated_specs'), {
          specs: next, updatedAt: serverTimestamp(), updatedBy: userName || 'embedded',
        });
      } catch(e) { console.error('Spec save failed:', e); }
      finally { setSaving(false); }
    }, 600);
  }

  function openEdit(key) {
    setEditKey(key);
    setEditDraft({ ...specs[key] });
  }

  function commitEdit() {
    const next = { ...specs, [editKey]: editDraft };
    setSpecs(next);
    saveSpecs(next);
    setEditKey(null);
  }

  function toggleValidate(key) {
    const next = { ...specs, [key]: { ...specs[key], validated: !specs[key].validated, validatedBy: !specs[key].validated ? userName : '', validatedAt: !specs[key].validated ? new Date().toISOString() : '' } };
    setSpecs(next);
    saveSpecs(next);
  }

  const validatedCount = Object.values(specs).filter(s => s.validated).length;

  return (
    <div>
      <SectionTitle title="Validated Specifications" sub="PRIMARY PAGE VALUES · ENGINEER-CONFIRMED · LINKED TO DAILY UPDATES" />
      <InfoBox>
        These are the <strong style={{color:T.accent}}>canonical validated values</strong> shown on the primary PCB page.
        When you complete a test or design verification (uploaded via Documentation tab), mark the relevant spec as{' '}
        <strong style={{color:T.green}}>Validated</strong> and link it to the update. Validated values are trusted for formal review.
      </InfoBox>

      {/* Validation progress */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
        <div style={{ flex:1 }}>
          <div style={{ height:4, background:T.border, borderRadius:2 }}>
            <div style={{ height:'100%', width:`${(validatedCount/Object.keys(specs).length)*100}%`,
              background:T.green, borderRadius:2, transition:'width 0.3s' }} />
          </div>
        </div>
        <span style={{ fontFamily:T.mono, fontSize:11, color:T.green }}>
          {validatedCount}/{Object.keys(specs).length} validated
        </span>
        {saving && <span style={{ fontFamily:T.mono, fontSize:9, color:T.yellow }}>saving…</span>}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:12 }}>
        {Object.entries(specs).map(([key, spec]) => (
          <div key={key} style={{
            background: spec.validated ? `${T.green}08` : T.panel,
            border:`1px solid ${spec.validated ? T.green+'44' : T.border}`,
            padding:16, position:'relative',
          }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
              <div style={{ fontFamily:T.mono, fontSize:10, color:T.dim, letterSpacing:1 }}>{spec.label.toUpperCase()}</div>
              <div style={{ display:'flex', gap:6 }}>
                <button onClick={() => openEdit(key)} style={{ background:'transparent', border:`1px solid ${T.border}`,
                  color:T.dim, fontFamily:T.mono, fontSize:8, padding:'2px 6px', cursor:'pointer' }}>EDIT</button>
                <button onClick={() => toggleValidate(key)} style={{
                  background: spec.validated ? `${T.green}20` : 'transparent',
                  border:`1px solid ${spec.validated ? T.green : T.border}`,
                  color: spec.validated ? T.green : T.dim,
                  fontFamily:T.mono, fontSize:8, padding:'2px 6px', cursor:'pointer' }}>
                  {spec.validated ? '✓ VALID' : 'VALIDATE'}
                </button>
              </div>
            </div>
            <div style={{ fontFamily:T.mono, fontSize:28, fontWeight:700, color: spec.validated ? T.green : T.accent }}>
              {spec.value}
              <span style={{ fontSize:13, fontWeight:400, color:T.dim, marginLeft:4 }}>{spec.unit}</span>
            </div>
            {spec.note && <div style={{ fontSize:11, color:T.dim, marginTop:6 }}>{spec.note}</div>}
            {spec.validated && (
              <div style={{ fontFamily:T.mono, fontSize:8, color:T.green, marginTop:8 }}>
                ✓ Validated by {spec.validatedBy || userName} {spec.validatedAt ? '· ' + new Date(spec.validatedAt).toLocaleDateString() : ''}
              </div>
            )}
            {spec.source && (
              <div style={{ fontFamily:T.mono, fontSize:8, color:T.accent, marginTop:4 }}>
                ↗ Linked: {updates.find(u => u.id === spec.source)?.title?.slice(0,30) || spec.source.slice(0,20)}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Edit modal */}
      {editKey && (
        <div style={{ position:'fixed', inset:0, background:'#000000aa', zIndex:200,
          display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:T.panel, border:`1px solid ${T.border}`, padding:24, width:'100%', maxWidth:400 }}>
            <div style={{ fontFamily:T.mono, fontSize:11, color:T.accent, marginBottom:16, letterSpacing:2 }}>
              EDIT SPEC — {specs[editKey]?.label.toUpperCase()}
            </div>
            <div style={{ display:'flex', gap:10, marginBottom:12 }}>
              <div style={{ flex:1 }}>
                <label style={{ fontFamily:T.mono, fontSize:9, color:T.dim, display:'block', marginBottom:4 }}>VALUE</label>
                <input value={editDraft.value} onChange={e => setEditDraft(d => ({...d, value: e.target.value}))}
                  style={{ width:'100%', background:'#0a1020', border:`1px solid ${T.border}`, color:T.bright,
                    fontFamily:T.mono, fontSize:18, padding:'8px', boxSizing:'border-box' }} />
              </div>
              <div style={{ width:80 }}>
                <label style={{ fontFamily:T.mono, fontSize:9, color:T.dim, display:'block', marginBottom:4 }}>UNIT</label>
                <input value={editDraft.unit} onChange={e => setEditDraft(d => ({...d, unit: e.target.value}))}
                  style={{ width:'100%', background:'#0a1020', border:`1px solid ${T.border}`, color:T.dim,
                    fontFamily:T.mono, fontSize:13, padding:'8px', boxSizing:'border-box' }} />
              </div>
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={{ fontFamily:T.mono, fontSize:9, color:T.dim, display:'block', marginBottom:4 }}>NOTE</label>
              <input value={editDraft.note||''} onChange={e => setEditDraft(d => ({...d, note: e.target.value}))}
                placeholder="Why this value, test conditions, constraints…"
                style={{ width:'100%', background:'#0a1020', border:`1px solid ${T.border}`, color:T.text,
                  fontFamily:T.sans, fontSize:12, padding:'8px', boxSizing:'border-box' }} />
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={{ fontFamily:T.mono, fontSize:9, color:T.dim, display:'block', marginBottom:4 }}>LINK TO UPDATE</label>
              <select value={editDraft.source||''} onChange={e => setEditDraft(d => ({...d, source: e.target.value}))}
                style={{ width:'100%', background:'#0a1020', border:`1px solid ${T.border}`, color:T.text,
                  fontFamily:T.mono, fontSize:11, padding:'8px' }}>
                <option value=''>— none —</option>
                {updates.map(u => <option key={u.id} value={u.id}>{u.title?.slice(0,50)||'untitled'} ({u.category})</option>)}
              </select>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={commitEdit} style={{ flex:1, padding:'9px', background:`${T.green}20`,
                border:`1px solid ${T.green}`, color:T.green, fontFamily:T.mono, fontSize:11, cursor:'pointer' }}>
                SAVE
              </button>
              <button onClick={() => setEditKey(null)} style={{ padding:'9px 16px', background:'transparent',
                border:`1px solid ${T.border}`, color:T.dim, fontFamily:T.mono, fontSize:11, cursor:'pointer' }}>
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════
const TABS = [
  { id:'overview',   label:'Overview'       },
  { id:'blockdiag',  label:'Block Diagram'  },
  { id:'schematic',  label:'Schematic Notes'},
  { id:'bom',        label:'BOM'            },
  { id:'pinmap',     label:'Pin Map'        },
  { id:'timing',     label:'Timing'         },
  { id:'firmware',   label:'Firmware'       },
  { id:'pcbnotes',   label:'PCB Notes'      },
  { id:'docs',       label:'Documentation'  },
  { id:'inventory',  label:'Inventory'      },
  { id:'validated',  label:'Validated Specs'},
];

export default function EMSCoilDriverPage({ userName }) {
  const [tab, setTab] = useState('overview');

  const sections = {
    overview:  <OverviewSection />,
    blockdiag: <BlockDiagSection />,
    schematic: <SchematicSection />,
    bom:       <BOMSection userName={userName} />,
    pinmap:    <PinMapSection />,
    timing:    <TimingSection />,
    firmware:  <FirmwareSection />,
    pcbnotes:  <PCBNotesSection />,
    docs:      <DocsSection userName={userName} />,
    inventory: <InventorySection userName={userName} />,
    validated: <ValidatedSpecsSection userName={userName} />,
  };

  return (
    <div style={{ background:T.bg, minHeight:'100vh', color:T.text, fontFamily:T.sans }}>
      {/* Header */}
      <div style={{
        background:`linear-gradient(135deg, #060912 0%, #0d1525 100%)`,
        borderBottom:`1px solid ${T.border}`,
        padding:'16px 28px', display:'flex', alignItems:'center', justifyContent:'space-between',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <div style={{ width:40, height:40, border:`1px solid ${T.accent}`, display:'flex',
            alignItems:'center', justifyContent:'center', fontFamily:T.mono, fontSize:11,
            fontWeight:900, color:T.accent, position:'relative' }}>
            SMC
            <span style={{ position:'absolute', inset:4, border:`1px solid ${T.accent}`, opacity:0.2 }} />
          </div>
          <div>
            <div style={{ fontFamily:T.mono, fontSize:13, fontWeight:700, color:T.accent, letterSpacing:2 }}>
              EMMS Coil Driver PCB
            </div>
            <div style={{ fontFamily:T.mono, fontSize:10, color:T.dim, marginTop:2 }}>
              SPACEMANCRAFT-1 · EP-ADCS SUBSYSTEM · REV A1
            </div>
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {[['REV A1',T.accent],['LEO 450km SSO',T.green],['RP2040',T.dim]].map(([l,c]) => (
            <span key={l} style={{ padding:'3px 9px', border:`1px solid ${c}50`, fontFamily:T.mono, fontSize:9, color:c, letterSpacing:1 }}>{l}</span>
          ))}
          {userName && (
            <span style={{ padding:'3px 9px', border:`1px solid ${T.yellow}40`, fontFamily:T.mono, fontSize:9, color:T.yellow, letterSpacing:1 }}>
              {userName.toUpperCase()}
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', background:T.panel, borderBottom:`1px solid ${T.border}`, padding:'0 28px', overflowX:'auto' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding:'11px 18px', fontFamily:T.mono, fontSize:10, letterSpacing:1.5, cursor:'pointer',
            background:'transparent', border:'none', whiteSpace:'nowrap', color: tab===t.id ? T.accent : T.dim,
            borderBottom:`2px solid ${tab===t.id ? T.accent : 'transparent'}`,
            transition:'color 0.15s',
          }}>
            {t.label.toUpperCase()}
            {t.id === 'docs' && <span style={{ marginLeft:5, background:T.accent, color:T.bg,
              borderRadius:8, padding:'0 5px', fontSize:8, fontFamily:T.mono }}>NEW</span>}
            {t.id === 'inventory' && <span style={{ marginLeft:5, background:T.orange, color:'#fff',
              borderRadius:8, padding:'0 5px', fontSize:8, fontFamily:T.mono }}>INV</span>}
            {t.id === 'validated' && <span style={{ marginLeft:5, background:T.green, color:T.bg,
              borderRadius:8, padding:'0 5px', fontSize:8, fontFamily:T.mono }}>SOP</span>}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ maxWidth:1200, margin:'0 auto', padding:'28px 28px 60px' }}>
        {sections[tab]}
      </div>

      {/* Global style overrides */}
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: ${T.bg}; }
        ::-webkit-scrollbar-thumb { background: ${T.border}; }
        input, textarea, select { outline: none; }
        a { color: ${T.accent}; }
      `}</style>
    </div>
  );
}
